/**
 * Map()
 * Implementation of Vue and Openlayers 5
 * Developer: Softoo Systems Co., Ltd.
 * nathakorn@softoo.co.th
 * 
 */
import { Vue, Component } from 'vue-property-decorator';

import { isFunction, isEmptyObject, isObject } from "@/common/utils";
import * as http from "@/common/http";

import * as proj from "ol/proj";
import proj4 from "proj4";
proj4.defs([
  [ "EPSG:32647", "+proj=utm +zone=47 +datum=WGS84 +units=m +no_defs" ],
  [ "EPSG:32648", "+proj=utm +zone=48 +datum=WGS84 +units=m +no_defs" ],
  [ "EPSG:4240",  "+proj=longlat +a=6377276.345 +b=6356075.41314024 +towgs84=214,836,303,0,0,0,0 +no_defs" ],
  [ "EPSG:24047", "+proj=utm +zone=47 +a=6377276.345 +b=6356075.41314024 +towgs84=214,836,303,0,0,0,0 +units=m +no_defs" ],
  [ "EPSG:24048", "+proj=utm +zone=48 +a=6377276.345 +b=6356075.41314024 +towgs84=214,836,303,0,0,0,0 +units=m +no_defs" ]
]);
import { register } from "ol/proj/proj4";
register(proj4);

import RBush from "ol/structs/RBush";
import { equals, getWidth, getTopLeft } from "ol/extent";
import { getUid } from "ol/util";

import * as control from "ol/control";
import * as interaction from "ol/interaction";
import * as condition from "ol/events/condition";
import { bbox } from "ol/loadingstrategy";
import { containsExtent } from "ol/extent";
import { outerWidth, outerHeight } from "ol/dom";
import OverlayPositioning from 'ol/OverlayPositioning.js';

import NativeMap from "ol/Map";
import View from "ol/View";
//import Attribution from "ol/control/Attribution";
import Feature from "ol/Feature";
import LayerVector from "ol/layer/Vector";
import LayerTile from "ol/layer/Tile";
import LayerImage from "ol/layer/Image";
import SourceVector from "ol/source/Vector";
import BingMaps from "ol/source/BingMaps";
import OSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ";
import WMTS from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import Tile from "ol/Tile";
import TileState from "ol/TileState";
import TileWMS from "ol/source/TileWMS";
import ImageWMS from "ol/source/ImageWMS";
import TileGrid from "ol/tilegrid/TileGrid";
import GeoJSON from "ol/format/GeoJSON";
import Polyline from "ol/format/Polyline";
import WKT from "ol/format/WKT";
import WMSCapabilities from 'ol/format/WMSCapabilities'; 
import WMTSCapabilities from 'ol/format/WMTSCapabilities'; 
import Projection from "ol/proj/Projection";
import Interaction from "ol/interaction/Interaction";
import Control from "ol/control/Control";
import Select from "ol/interaction/Select";
import Snap from "ol/interaction/Snap";
import Collection from "ol/Collection";
import Cluster from "ol/source/Cluster";
import Overlay from "ol/Overlay";
import Popup from "./Popup.vue";
import HoverInfo from "./HoverInfo.vue";

//import Draw from "ol/interaction/Draw";
import Geometry from "ol/geom/Geometry";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import Polygon from "ol/geom/Polygon";
import MultiPolygon from "ol/geom/MultiPolygon";

import { Ring } from "./Ring";
import { Measurer } from "./tools/Measurer";
import { UndoRedo } from "./tools/UndoRedo";
import { Drawer } from "./tools/Drawer";
import { ConvexHull } from "./tools/ConvexHull";
import { ModifyFeature } from "./tools/ModifyFeature";
import { MoveFeature } from "./tools/MoveFeature";
import { TranslateFeature } from "./tools/TranslateFeature";
import { Indian75ToWgs84 } from "./tools/Indian75ToWgs84";
import { CutHole } from "./tools/CutHole";
import { Splitter } from "./tools/Splitter";
import { DeleteFeature } from "./tools/DeleteFeature";

let OpenLocationCode = require("open-location-code").OpenLocationCode;

// for use google map api
//import { loader as GoogleMapApiLoader } from "./GoogleMapApiLoader";
import OLGoogleMaps from 'olgm/OLGoogleMaps.js';
import GoogleLayer from 'olgm/layer/Google.js';
import GoogleMap from "./GoogleMap.js";

/**
 * override Openlayers
 */
RBush.prototype.update = function update (extent, value) {
  let item = this.items_[getUid(value)];
  if (item) { // added this condition. there is something abnormal when use ModifyFeature but could not find it
    let bbox = [item.minX, item.minY, item.maxX, item.maxY];
    if (!equals(bbox, extent)) {
      this.remove(value);
      this.insert(extent, value);
    }
  }
};

Overlay.prototype['panIntoView'] = function panIntoView() {
  let map = this.getMap();

  if (!map || !map.getTargetElement()) {
    return;
  }
  let mapRect = this.getRect(map.getTargetElement(), map.getSize());

  // added following to adjust mapRect with view padding
  let $map = map.SOFTOO_MAP;
  let padding = $map.ViewPadding; //viewProps.padding || [0,0,0,0];
  mapRect[0] += padding[3]; // left
  mapRect[1] += padding[0]; // top
  mapRect[2] -= padding[1]; // right
  mapRect[3] -= padding[2]; // bottom
  // added above 

  let element = this.getElement();
  let overlayRect = this.getRect(element, [outerWidth(element), outerHeight(element)]);

  let margin = this.autoPanMargin;
  if (!containsExtent(mapRect, overlayRect)) {
    // the overlay is not completely inside the viewport, so pan the map
    let offsetLeft = overlayRect[0] - mapRect[0];
    let offsetRight = mapRect[2] - overlayRect[2];
    let offsetTop = overlayRect[1] - mapRect[1];
    let offsetBottom = mapRect[3] - overlayRect[3];

    let delta = [0, 0];
    if (offsetLeft < 0) {
      // move map to the left
      delta[0] = offsetLeft - margin;
    } else if (offsetRight < 0) {
      // move map to the right
      delta[0] = Math.abs(offsetRight) + margin;
    }
    if (offsetTop < 0) {
      // move map up
      delta[1] = offsetTop - margin;
    } else if (offsetBottom < 0) {
      // move map down
      delta[1] = Math.abs(offsetBottom) + margin;
    }

    if (delta[0] !== 0 || delta[1] !== 0) {
      let center = /** @type {module:ol/coordinate~Coordinate} */ (map.getView().getCenter());
      let centerPx = map.getPixelFromCoordinate(center);
      let newCenterPx = [
        centerPx[0] + delta[0],
        centerPx[1] + delta[1]
      ];
      let self = this;
      let view = map.getView();
      view.animate({
        center: map.getCoordinateFromPixel(newCenterPx),
        duration: this.autoPanAnimation.duration,
        easing: this.autoPanAnimation.easing
      },(state) => {
        self.onViewAnimationEnd(view, state);
      });
    }
  }
};

SourceVector.prototype.loadFeatures = function loadFeatures (extent, resolution, projection) {
  // add following condition in order to load feature only when comes down to the expected resolution
  // in layer file we set minResolution to MAX_RESOLUTION_4_EDITING * 256 in order to allow 
  // showing features that are being edited
  if (resolution <= MAX_RESOLUTION_4_EDITING) {
    let this$1 = this;
    let loadedExtentsRtree = this.loadedExtentsRtree_;
    let extentsToLoad = this.strategy_(extent, resolution);
    this.loading = false;
    let loop = function ( i, ii ) {
      let extentToLoad = extentsToLoad[i];
      let alreadyLoaded = loadedExtentsRtree.forEachInExtent(extentToLoad,
        /**
         * @param {{extent: module:ol/extent~Extent}} object Object.
         * @return {boolean} Contains.
         */
        function(object) {
          return containsExtent(object.extent, extentToLoad);
        });
      if (!alreadyLoaded) {
        this$1.loader_.call(this$1, extentToLoad, resolution, projection);
        loadedExtentsRtree.insert(extentToLoad, {extent: extentToLoad.slice()});
        this$1.loading = true;
      }
    };

    for (let i = 0, ii = extentsToLoad.length; i < ii; ++i) loop( i, ii );
  }
};
/**
 * Somehow the overlay element size is not stable. it varies with 1px
 * So we force to adjust this
 * @param {module:ol/pixel~Pixel} pixel The pixel location.
 * @param {module:ol/size~Size|undefined} mapSize The map size.
 * @protected
 */
let overlayElementWith:number = 0;
let overlayElementHeight:number = 0;
Overlay.prototype['updateRenderedPosition'] = function updateRenderedPosition (pixel, mapSize) {
  let style = this.element.style;
  let offset = this.getOffset();

  let positioning = this.getPositioning();

  this.setVisible(true);

  let offsetX = offset[0];
  let offsetY = offset[1];
  if (positioning == OverlayPositioning.BOTTOM_RIGHT ||
      positioning == OverlayPositioning.CENTER_RIGHT ||
      positioning == OverlayPositioning.TOP_RIGHT) {
    if (this.rendered.left_ !== '') {
      this.rendered.left_ = style.left = '';
    }
    let right = Math.round(mapSize[0] - pixel[0] - offsetX) + 'px';
    if (this.rendered.right_ != right) {
      this.rendered.right_ = style.right = right;
    }
  } else {
    if (this.rendered.right_ !== '') {
      this.rendered.right_ = style.right = '';
    }
    if (positioning == OverlayPositioning.BOTTOM_CENTER ||
        positioning == OverlayPositioning.CENTER_CENTER ||
        positioning == OverlayPositioning.TOP_CENTER) {
      if (Math.abs(this.element.offsetWidth - overlayElementWith) > 1) {
        overlayElementWith = this.element.offsetWidth;
      }
      //offsetX -= this.element.offsetWidth / 2;
      offsetX -= overlayElementWith / 2;
    }
    let left = Math.round(pixel[0] + offsetX) + 'px';
    if (this.rendered.left_ != left) {
      this.rendered.left_ = style.left = left;
    }
  }
  if (positioning == OverlayPositioning.BOTTOM_LEFT ||
      positioning == OverlayPositioning.BOTTOM_CENTER ||
      positioning == OverlayPositioning.BOTTOM_RIGHT) {
    if (this.rendered.top_ !== '') {
      this.rendered.top_ = style.top = '';
    }
    let bottom = Math.round(mapSize[1] - pixel[1] - offsetY) + 'px';
    if (this.rendered.bottom_ != bottom) {
      this.rendered.bottom_ = style.bottom = bottom;
    }
  } else {
    if (this.rendered.bottom_ !== '') {
      this.rendered.bottom_ = style.bottom = '';
    }
    if (positioning == OverlayPositioning.CENTER_LEFT ||
        positioning == OverlayPositioning.CENTER_CENTER ||
        positioning == OverlayPositioning.CENTER_RIGHT) {
      if (Math.abs(this.element.offsetHeight - overlayElementHeight) > 1) {
        overlayElementHeight = this.element.offsetHeight;
      }
      //offsetY -= this.element.offsetHeight / 2;
      offsetY -= overlayElementHeight / 2;
    }
    let top = Math.round(pixel[1] + offsetY) + 'px';
    if (this.rendered.top_ != top) {
      this.rendered.top_ = style.top = top;
    }
  }
};

export enum EGoogleMapType {
  NORMAL = 1,    //kGMSTypeNormal
  SATELLITE,     //kGMSTypeSatellite
  TERRAIN,       //kGMSTypeTerrain
  HYBRID,        //kGMSTypeHybrid
  NONE           //kGMSTypeNone
};

import { IMap, IDMS, 
         IMapViewProps, IMapViewExtent, IMapViewInfo, 
         IFeatureInfo, IPopUpInfo, IHoverInfo,
         IBaseMapProvider, IBaseMapSource, IMeasurer, 
         Extent, Coordinate, TileCoord, IMapLayer, IMapLayerSource,
         IFlashPin, IMapCoordinate, IOverlay, 
         IGeoJSON, IFormatOptions, IUndoRedo, 
         IFeatureToSave, IFeatureToSaveByIntersects, ILayerForEdit
} from "./types";

import Style from "ol/style/Style";
import * as STYLE from "./Style";
import { UTM, IUTMCoordinate } from "./utm";

import { BASEMAP, DEFAULT_BASEMAP } from "./data/basemaps";

//const MAX_RESOLUTION_4_EDITING = 4.777314267158508 / 2;
let MAX_RESOLUTION_4_EDITING:number = Number.MAX_VALUE;
const PIXEL_TOLERANCE = 8; //Openlayers default is 10;
const INCHES_PER_METER = 39.37;

const FLASH_DURATION = 2500;
const EASE_OUT = t => 1 - Math.pow(1 - t, 3)

export const MAPS_URL = http.urls.gis.url;
export const MV_ANIMATION_DURATION = 10;                       
export const URL_HASH_FACTOR = (Math.sqrt(5) - 1) / 2;
export const VIEW_PADDING: Extent = [0, 0, 0, 0]; // [top,right,bottom,left] in pixel
export const MAP_EXTENT: Extent = [95.854, 5.299, 106.064, 20.533]; // whole Thailand
export const DEFAULT_VIEW_CENTER: Coordinate = [(MAP_EXTENT[0]+MAP_EXTENT[2])/2,(MAP_EXTENT[1]+MAP_EXTENT[3])/2];
export const DEFAULT_MAP_OPTIONS = {
  controls: control.defaults({
    zoom: false, 
    rotate: false,
    attribution: true,
    attributionOptions: {
      collapsible:true, 
      collapsed:false,
      collapseLabel: '◁' /* since we put attributions at left side*/
    }
  }),
  loadTilesWhileInteracting: true,            
  view: new View({
    center: proj.transform(DEFAULT_VIEW_CENTER, 'EPSG:4326', 'EPSG:3857'),
    zoom: 12
  }),
  keyboardEventTarget:'Element'
};

@Component
export default class Map extends Vue {
  // these are map props set when mounted() in MapUI.vue
  protected viewProps: IMapViewProps = {
    extent: null,
    padding: null,
    center: null,
    zoom: null
  };

  map: IMap = {
    Id: '',
    XDPI: 96, // set during created() in MapUI.vue
    YDPI: 96, // set during created() in MapUI.vue
    Action: {
      name: null,//'getInfo',
      params: null
    },
    HasGoogleMap: false,
    BaseMaps: BASEMAP,
    MapLayers: [],
    FlashPins: [],
    NativeMap: null,
    GoogleMap: null
  };
  
  async loadJsScript(url:string) : Promise<boolean> {
    return new Promise((resolve,reject) => {
      try {
        let loaded = false;
        let src, scripts = document.getElementsByTagName('script');
        if (scripts && scripts.length>0) {
          for (let i=0; i<scripts.length && !loaded; ++i) {
            src = scripts[i].getAttribute('src');
            loaded = !!src && !!src.match(/maps.googleapis.com/i);
          }
        }
        if (!loaded) {
          let script = document.createElement('script');
          script.setAttribute("type","text/javascript");
          script.setAttribute("src", url);
          document.getElementsByTagName("head")[0].appendChild(script);
        }
        loaded = !!(window['google'] && window['google'].maps);
        let wait = 0;
        let interval = setInterval(() => {
          loaded = !!(window['google'] && window['google'].maps);
          if (loaded || wait>10) {
            clearInterval(interval);
            resolve(loaded);
          }
          wait++;
        },500);
      } catch (error) {
        reject(error);
      }
    });
  };

  getGoogleMapTypeId(google:IBaseMapSource) : any {
    let mapTypeId = window['google'].maps.MapTypeId.ROADMAP;
    switch (google.Type) {
      case 'Road':
        break;
      case 'Terrain':
        mapTypeId = window['google'].maps.MapTypeId.TERRAIN;
        break;
      case 'Satellite':
        mapTypeId = window['google'].maps.MapTypeId.SATELLITE;
        break;
      case 'Hybrid':
          mapTypeId = window['google'].maps.MapTypeId.HYBRID;
        break;
    }
    return mapTypeId;
  }

  createGoogleLayer2(google:IBaseMapSource) : GoogleLayer {
    google.Layer = new GoogleLayer({
      mapTypeId: this.getGoogleMapTypeId(google)
    });
    this.map.HasGoogleMap = true;
    return google.Layer;
  }

  createGoogleLayerX(google:IBaseMapSource, apiKey?:string) : GoogleMap {
    google.Layer = new GoogleMap({
      mapTypeId: this.getGoogleMapTypeId(google)
    });
    this.map.HasGoogleMap = true;
    return google.Layer;
  }

  createGoogleLayer(google:IBaseMapSource, apiKey?:string) : LayerTile {
    // TODO: following is technially good, but it violates Google terms
    // Google allows to access to its map via Google Map API only
    google.Layer = new LayerTile({
      source: new OSM({
        attributions: [
          '©Google&nbsp;<a href="https://developers.google.com/maps/terms" target="_new">Terms of Use.</a>'
        ],
        url: google.Url
      })
    });
    this.map.HasGoogleMap = true;
    return google.Layer;
  };

  createBingLayer(bing:IBaseMapSource, apiKey:string) : LayerTile {
    let culture = navigator ? navigator.language : 'th_TH';
    bing.Layer = new LayerTile({
      preload: Infinity,
      source: new BingMaps({
        key: apiKey, 
        imagerySet: bing.Type,
        culture: culture
      })
    });
    return bing.Layer;
  };

  createTMSLayer(tms:IBaseMapSource, apiKey:string) : LayerTile {
    tms.Layer = new LayerTile({
      source: new XYZ({
        attributions: tms.Attributions || undefined,
        //key: apiKey, // ??
        tileGrid: new TileGrid({
          origin: tms.Origin, //IMPORTANT: [bottom,left] vs [top,left] for XYZ
          resolutions: tms.Resolutions || []
        }),
        tileUrlFunction: function (coordinate: TileCoord, pixelRatio: number, proj: Projection) {
          try {
            let z = coordinate[0];
            let x = coordinate[1];
            let y = coordinate[2];
            //y = (1 << z) - y - 1;  //xyz to tms, or vice versa >> no need to transform any more since we have already specified the tms.Origin
            //if (tms.Type.match(/Thaichote/)) {
            //    z--; //???GISTDA Error??? //depends on tms.Resolutions that must start at 78271.516964
            //}
            //let url:string = selectUrl('' + x + y + z, tms.Urls);
            return (tms.Url+'').replace("{z}", "" + z)
                    .replace("{x}", "" + x)
                    .replace("{y}", "" + y);
          } catch (e) {
            // todo: return a predefined error image 256x256 pixels
            //return undefined;
            return ''; // todo, a predefined url to an image showing error
          }
        }
      })
    });
    return tms.Layer;
  };

  createXYZLayer(xyz:IBaseMapSource, apiKey?:string) : LayerTile {
    xyz.Layer = new LayerTile({
      source: new XYZ({
        url: xyz.Url + (apiKey ? ('?apikey=' + apiKey) : '')
      })
    });
    return xyz.Layer;
  };

  createWMTSLayer(wmts) : LayerTile {
    if (!this.map.NativeMap) return;
    let url = wmts.url || wmts.Url || MAPS_URL + 'wmts';
    if (!(wmts.url || wmts.Url) || (url.indexOf(http.APP_HOST)>0)) {
      url += '?apiid=' + http.APIID + '&key=' + http.APPKEY;
    }
    let projectionExtent = this.Projection.getExtent();
    let size = getWidth(projectionExtent) / 256;
    let nZoom = wmts.maxZoomLevel || 15;
    let resolutions = new Array(nZoom);
    let matrixIds = new Array(nZoom);
    for (let z = 0; z < nZoom; ++z) {
      // generate resolutions and matrixIds arrays for this WMTS
      resolutions[z] = size / Math.pow(2, z);
      matrixIds[z] = (wmts.wmtsMatrixSet ? (wmts.wmtsMatrixSet + ':') : '') + z;
    }
    let options = {
      source: new WMTS({
        url: url,
        layer: wmts.featureType,
        matrixSet: wmts.wmtsMatrixSet || 'EPSG:3857',
        projection: wmts.wmtsEPSG || 'EPSG:3857',
        tileGrid: new WMTSTileGrid({
          origin: getTopLeft(projectionExtent),
          resolutions: resolutions,
          matrixIds: matrixIds
        }),
        format: 'image/png',
        style: '',
        wrapX: true
      })
    };
    if (wmts.authen) {
      options.source.setTileLoadFunction((tile:Tile, url:string) => {
        this.requestWithBasicAuthen(tile,url,wmts.authen);
      });
    }
    return new LayerTile(options);
  }

  createWMSLayer(wms) {
    wms.params = wms.params || {};
    if (wms.useGwc || (wms.url && wms.url.match(/\/gwc\//i))) {
      wms.params.SRS = this.Projection.getCode(); //'EPSG:3857'; // GeoServer GWC requires SRS instead of CRS
    }
    /*let hasSRS:boolean = false;
    for (let p in wms.params) {
      hasSRS = !!p.match(/^SRS$/i);
      if (hasSRS) {
        break;
      }
    }
    if (!hasSRS) {
      wms.params.SRS = this.Projection.getCode(); //'EPSG:3857'; // somehow, we force to submit SRS
    }*/
    let url = wms.url || (MAPS_URL + (wms.useGwc===true ? 'gwc' : 'wms'));
    if (!wms.url || (url.indexOf(http.APP_HOST)>0)) {
      wms.params.APIID = http.APIID;
      wms.params.KEY = http.APPKEY;
    }
    let options:any = {};
    if (wms.extent) { //e.g: [-13884991, 2870341, -7455066, 6338219]
      options.extent = wms.extent;
    }
    options.minResolution = wms.minResolution || undefined;
    options.maxResolution = wms.maxResolution || undefined;
    options.zIndex = wms.zIndex || 0;
    options.visible = (wms.visible!==false);
    if (wms.params['TILED'.toUpperCase()]===false) {
      options.source = new ImageWMS({
        url: url,
        params: Object.assign({}, wms.params || {}, wms.params2 || {}), // IMPORTANT: to clone wms.params
        ratio: 1,
        serverType: wms.serverType
      });
      return new LayerImage(options);
    } else {
      options.source = new TileWMS({
        url: url,
        crossOrigin: (wms.crossOrigin || 'anonymous'),
        params: Object.assign({}, wms.params || {}, wms.params2 || {}), // IMPORTANT: to clone wms.params
        serverType: wms.serverType,
        transition: wms.transition || 0
      });
      if (wms.authen) {
        options.source.setTileLoadFunction((tile:Tile, url:string) => {
          this.requestWithBasicAuthen(tile,url,wms.authen);
        });
      }
      return new LayerTile(options);
    }
  }

  private async requestWithBasicAuthen(tile:Tile,url:string,credential:{username:string;password:string}) {
    try {
      let headers = {};
      headers[http.HEADER_CONTENT_TYPE] = 'image/png';
      headers[http.HEADER_AUTH] = 'Basic ' + btoa(credential.username + ':' + credential.password);
      let image = await http.request(url, {
        headers: headers
      });
      if (!!(image && image.type.match(/image\/png/i))) {
        if (!!tile['getImage']) {
          tile['getImage']().src = URL.createObjectURL(image);
        }
      } else {
        tile.setState(TileState.ERROR);
      }
    } catch (error) {
      tile.setState(TileState.ERROR);
    }
  }

  createWFSLayer(wfs:IMapLayerSource) {
    let url = wfs.url || MAPS_URL + 'wfs';
    let loader = async function (extent, resolution, projection) {
      try {
        /*let data = await http.fetchData(url + 'wfs', {
          method: 'POST',
          body: JSON.stringify({
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typename: wfs.featureType,
            srsname: 'EPSG:3857',
            outputFormat: 'application/json',
            bbox: extent.join(',') + ',EPSG:3857'
          })
        });*/
        let u = url + '?service=WFS&version=1.1.0&request=GetFeature' +
              '&typename=' + wfs.featureType + 
              '&srsname=EPSG:3857' +
              '&key=' + http.APPKEY + 
              '&outputFormat=application/json';
        //if (wfs.filterOptions) {
        //  wfs.filterOptions.forEach((filter) => {
        //    u += '&' + filter
        //  });
          /*for (let f in filterOptions) {
            if (filterOptions[f]) {
              u += '&' + f + '=' + filterOptions[f];
            }
          }*/
        //}
        //bbox and cql_filter both specified but are mutually exclusive
        if (!!wfs.params && !!wfs.params.CQL_FILTER) {
          u += '&CQL_FILTER=' + wfs.params.CQL_FILTER;
        } else {
          u += '&bbox=' + extent.join(',') + ',EPSG:3857';
        }
        let options = null;
        if (wfs.authen) {
          options = {
            headers:{}
          }
          options.headers[http.HEADER_AUTH] = 'Basic ' + btoa(wfs.authen.username + ':' + wfs.authen.password);
        }
        let data = await http.request(u, options);
        let format:GeoJSON = <GeoJSON> this.getFormat();
        wfsSource.addFeatures(format.readFeatures(data));
      } catch (error) {
        console.log('createWFSLayer Error: ');
        console.log(error);
      }
    }
    let wfsSource = new SourceVector({
      loader: loader,
      format: new GeoJSON(),
      strategy: bbox
    });

    if (wfs.clusterDistance) {
      wfsSource = new Cluster({
        distance: wfs.clusterDistance,
        source: wfsSource
      });
    }

    /*let style = function(feature:Feature, resolution:number) {
      let s = wfs.style;
      if (isFunction(s)) {
        s = s(feature, resolution);
      }
      return STYLE.create(feature, resolution, s);
    }*/
    let style;
    if (wfs.style && isFunction(wfs.style)) {
      style = function(feature:Feature, resolution:number) {
        let s = (<Function> wfs.style)(feature, resolution);
        return STYLE.create(feature, resolution, s);
      }
    } else {
      style = wfs.style || STYLE.Default;
    }

    if (wfs.maxEditResolution && MAX_RESOLUTION_4_EDITING>wfs.maxEditResolution) {
      MAX_RESOLUTION_4_EDITING = wfs.maxEditResolution;
    }

    let layer:LayerVector = new LayerVector({
      source: wfsSource,
      style: style,
      minResolution: wfs.minResolution || undefined,
      maxResolution: wfs.maxResolution || undefined,
      visible: wfs.visible!==false,
      zIndex: wfs.zIndex || 0
    });
    layer['canEdit'] = (wfs.canEdit===true) && (!!wfs.geometryType.match(/^Point$|^Line$|^Polygon$/));
    layer['canHover'] = wfs.canHover || layer['canEdit'] || (wfs.canQuery!==false);
    layer['canSnap'] = wfs.canSnap || layer['canEdit'];
    if (layer['canEdit']) {
      layer['geometryType'] = wfs.geometryType;
    }
    //wfs.mapLayer = layer;
    return layer;
  }

  createVectorLayer(vtr:IMapLayerSource) {
    let features;
    if (vtr.geoJSONs) {
      features = vtr.geoJSONs.map((gj) => {
        return this.GeoJSONToFeatures(gj)[0];
      });
      delete vtr.geoJSONs;
    }
    let source = new SourceVector();
    if (vtr.clusterDistance) {
      source = new Cluster({
        distance: vtr.clusterDistance,
        source: source
      });
      if (features) {
        source['source'].addFeatures(features);
      }
    } else if (features) {
      source.addFeatures(features);
    }
    let style;
    if (vtr.style) {
      if (isFunction(vtr.style)) {
        style = function(feature:Feature, resolution:number) {
          let s = (<Function>vtr.style)(feature, resolution);
          return STYLE.create(feature, resolution, s);
        }
      } else if (vtr.style instanceof Style) {
        style = vtr.style;
      } else if (isObject(vtr.style)) {
        let s = {};
        ['geometry','fill','image','renderer','stroke','text','zIndex']
        .forEach((p) => {
          let v = vtr.style[p] || vtr.style[p + '_'];
          if (!!v) {
            s[p] = v;
          }
        });
        style = new Style(s);
      } else {
        style = vtr.style;
      }
    } else {
      style = STYLE.Default;
    }
    let layer = new LayerVector({
      source: source,
      style: style
    });
    layer['canEdit'] = (vtr.canEdit===true) && (!!vtr.geometryType.match(/^Point$|^Line$|^Polygon$/));
    layer['canHover'] = vtr.canHover || layer['canEdit'];
    layer['canSnap'] = vtr.canSnap || layer['canEdit'];
    if (layer['canEdit']) {
      layer['geometryType'] = vtr.geometryType;
    }
    return layer;
  };
  
  createBaseMap(provider:IBaseMapProvider, map:IBaseMapSource) : IBaseMapSource {
    switch (provider.Name.toLowerCase()) {
    case 'internal':
      map.Layer = this.createWMSLayer(map);
      map.Layer.setOpacity(0);
      break;
    case 'gistda':
      if (map.Type=='Planet') {
        //map.Layer = this.createWMTSLayer(map);
        this.createXYZLayer(map);
      } else {
        this.createTMSLayer(map, provider.Key);
      }
      map.LegendLayer = map.Layer;
      break;
    case 'google':
      /**
       * Limitation: olgm does not support ol style function !!!
       * so it does not work for this project that uses style function
       **/
      //dont change following order
      map.LegendLayer = this.createGoogleLayer(map, provider.Key);
      //this.createGoogleLayer2(map); //force to call Google Map API
      this.createGoogleLayer(map); 
      break;
    case 'bing':
      //map.Layer = 
      this.createBingLayer(map, provider.Key);
      map.LegendLayer = map.Layer;
      break;
    case 'osm':
      this.createXYZLayer(map, provider.Key);
      map.LegendLayer = map.Layer;
      break;
    }
    return map;
  };

  createBaseMaps(basemap:string) {
    if (http.APP_HOST.match(/\.mwa\.co\.th$|^gisgovs\./i) || 
      http.APP_HOST=='27.254.170.25' || http.APP_HOST=='115.31.184.240') {
      for (let i=0; i<BASEMAP.length; i++) {
        if (BASEMAP[i].Name.match(/^google$/i)) {
          // mwa requires no Google Map
          BASEMAP.splice(i,1);
          break;
        }
      }
    }
    basemap = (basemap || DEFAULT_BASEMAP).toLowerCase();
    let defaultBaseMap;
    BASEMAP.forEach((provider) => {
      provider.Maps.forEach((map) => {
        map.Id = (map.Id || (provider.Name + '.' + map.Type)).toLowerCase();
        let bm = this.createBaseMap(provider, map);
        if (basemap==map.Id) {
          defaultBaseMap = bm; 
        }
      });
    }, this);
    return defaultBaseMap;
  };

  createBaseMapViews(center, zoom, rotation) {
    let noInteraction = interaction.defaults({
      altShiftDragRotate: false,
      constrainResolution: false,
      doubleClickZoom: false,
      keyboard: false,
      mouseWheelZoom: false,
      shiftDragZoom: false,
      dragPan: false,
      pinchRotate: false,
      pinchZoom: false
    });
    let noControls = control.defaults({
      zoom: false,
      rotate: false,
      attribution: false
    });
    BASEMAP.forEach((provider) => {
      provider.Maps.forEach((map) => {
        if (!map.LegendLayer) {
          map.Id = (map.Id || (provider.Name + '.' + map.Type)).toLowerCase();
          this.createBaseMap(provider, map);
        }
        map.NativeMap = new NativeMap({
          target: map.Id,
          interactions: noInteraction,
          controls: noControls,
          layers: [ map.LegendLayer ],       
          view: new View({
            center: center,
            zoom: zoom || 12,
            rotation: rotation
          })
        });
        if (!!provider.Name.match(/Google/i) && map.LegendLayer instanceof GoogleMap) {
          map.LegendLayer.gMap_ = new window['google'].maps.Map(
            map.NativeMap.getTargetElement(), {
            disableDefaultUI: true,
            disableDoubleClickZoom: true,
            draggable: false,
            keyboardShortcuts: false,
            mapTypeId: this.getGoogleMapTypeId(map),
            scrollwheel: false,
            streetViewControl: false,
            animatedZoom: false
          });
          map.LegendLayer.olMap_ = map.NativeMap;
        }
      }, this);
    }, this);
  };

  refreshBaseMapLists() {
    BASEMAP.forEach((provider) => {
      provider.Maps.forEach((map) => {
        if (map.NativeMap) {
          map.NativeMap.updateSize(); // force to redraw
        }
      });
    }, this);
  };

  /**
   * Every nativeLayer has one source and vice versa 
   * but one source/nativeLayer could be consumed in 
   * various map views for display
   * In a nutshell: followings are customized in order to
   * trace a feature:
   * feature['NATIVE_SOURCE']['SOFTOO_SOURCE'].nativeLayer
   * feature['NATIVE_SOURCE']['NATIVE_LAYERS']['SOFTOO_MAPS']
   * @param nativeLayer 
   */
  createReferences(nativeLayer) {
    let sm = nativeLayer['SOFTOO_MAPS'];
    if (!sm) {
      nativeLayer['SOFTOO_MAPS'] = [this];  // customized
    } else {
      if (sm.indexOf(this)==-1) {
        sm.push(this);
      }
    }
    let nativeSource = nativeLayer.getSource();
    nativeSource['NATIVE_LAYERS'] = nativeSource['NATIVE_LAYERS'] || [];
    if (nativeSource['NATIVE_LAYERS'].indexOf(nativeLayer)==-1) {
      nativeSource['NATIVE_LAYERS'].push(nativeLayer);
    }
    if (nativeLayer instanceof LayerVector) {
      nativeSource.on('addfeature', (event) => {
        let feature:Feature = event.feature;
        let source:IMapLayerSource;
        if (!!feature['NATIVE_SOURCE']) {
          source = feature['NATIVE_SOURCE'].SOFTOO_SOURCE;
        }
        if (!source) {
          source = event.target['NATIVE_LAYERS'][0]['SOFTOO_SOURCE'];
          feature['NATIVE_SOURCE'] = event.target;
          feature['NATIVE_SOURCE']['SOFTOO_SOURCE'] = source;
        }
        // splitted (new) feature from Splitter were already added to UndoRedo
        if (!(feature.get('gid') || feature.getId()) && 
          !feature.get('STATE') && source && source.canEdit) {
          // this is a newly digitized feature
          let sm = event.target['NATIVE_LAYERS'][0]['SOFTOO_MAPS'][0];
          sm.addUndo(event.feature, 'Insert');
          // used to check if shape has been enlarged or shrinked
          // reset it on saved
          if (feature.get('area_sqm')!==undefined) {
            feature.set('originalAreaSqm', feature.get('area_sqm'));
          }
        }
      });
    }
  }

  /**
   * @param mLayers 
   * @param parent 
   */
  createOverlayLayers(mLayers:IMapLayer[], parent?:IMapLayer) {
    mLayers.forEach((ml:IMapLayer) => {
      ml.parent = parent || ml.parent;
      let sm = ml['SOFTOO_MAPS'];
      if (!sm) {
        ml['SOFTOO_MAPS'] = [this];  // customized
      } else {
        if (sm.indexOf(this)==-1) {
          sm.push(this);
        }
      }
      let layer;
      if (ml.mapSources) {
        ml.mapSources.forEach((ms) => {
          if (!ms.nativeLayer) {
            switch (ms.service.toUpperCase()) {
            case "VECTOR":
              layer = this.createVectorLayer(ms);
              break;
            case 'WFS':
              layer = this.createWFSLayer(ms);
              break;
            case 'WMS':
              layer = this.createWMSLayer(ms);
              break;
            case 'WMTS':
              layer = this.createWMTSLayer(ms);
              break;
            }
            if (ms.opacity!==undefined) {
              layer.setOpacity(ms.opacity);
            }
            if (ms.events) {
              let source = layer.getSource();
              for (let e in ms.events) {
                source.on(e, ms.events[e]);
              }
            }
            layer.geomType = ml.geomType;
            ms.nativeLayer = layer;
            ms.nativeLayer['SOFTOO_SOURCE'] = ms;
            ml.visible = ml.visible!==undefined ? ml.visible : (ms.visible!==false);
            ms.nativeLayer.setVisible(ml.visible);
            
            switch (ms.service.toUpperCase()) {
            case "VECTOR":
              //the later in MapLayers, the topper to be shown on map
              this.map.VectorLayers = this.map.VectorLayers || [];
              if (this.map.VectorLayers.indexOf(layer)==-1) {
                this.map.VectorLayers.unshift(layer);
              }
              break;
            case 'WFS':
              //the later in MapLayers, the topper to be shown on map
              this.map.VectorLayers = this.map.VectorLayers || [];
              if (this.map.VectorLayers.indexOf(layer)==-1) {
                this.map.VectorLayers.unshift(layer);
              }
              break;
            case 'WMS':
            case 'WMTS':
              this.map.TileLayers = this.map.TileLayers || [];
              if (this.map.TileLayers.indexOf(layer)==-1) {
                this.map.TileLayers.push(layer);
              }
              break;
            }
          }
          ms.mapLayers = ms.mapLayers || [];
          if (ms.mapLayers.indexOf(ml)==-1) {
            ms.mapLayers.push(ml);
          }
        });
      }
      if (ml.children && ml.children.length>0) {
        this.createOverlayLayers(ml.children, ml);
      } else if (ml.legend && ml.legend.length>0) {
        this.createOverlayLayers(ml.legend, ml);
      }
    },this);

    //ensure that point editing layers are above drawing layer
    //and line and polygon layers are under drawing layer
    let editLayers = this.getEditingLayers();
    editLayers.Point.forEach((layer,index) => {
      layer.setZIndex(1000 + index);
    });
    let layers = [...editLayers.Polygon,...editLayers.Line];
    let zIndex = 999 - layers.length;
    layers.forEach((layer,index) => {
      layer.setZIndex(zIndex + index);
    });
  };
  
  createWorkingLayersAndControls() {
    // create overlay layers and internal vector layers
    // this.map.NativeMap should have already created, such as in MapUI.mounted()
    this.map.TileLayers = null;
    this.map.VectorLayers = [];
    this.map.SearchFeatureLayer = this.createVectorLayer({
      style: STYLE.Search,
      canSnap: false
    });
    this.map.SearchFeatureLayer.setZIndex(990);
    this.map.VectorLayers.unshift(this.map.SearchFeatureLayer);
    this.map.AoiLayer = this.createVectorLayer({
      style: STYLE.Aoi,
      canSnap: false
    });
    this.map.AoiLayer.setZIndex(991);
    this.map.VectorLayers.unshift(this.map.AoiLayer);
    this.map.SelectedFeatureLayer = this.createVectorLayer({
      style: STYLE.Select,
      canSnap: false
    });
    this.map.SelectedFeatureLayer.setZIndex(992);
    this.map.VectorLayers.unshift(this.map.SelectedFeatureLayer);
    this.map.DrawingLayer = this.createVectorLayer({
      style: STYLE.Draw,
      canSnap: true
    });
    this.map.DrawingLayer.setZIndex(999);
    this.map.VectorLayers.unshift(this.map.DrawingLayer);

    this.map.VectorLayers.forEach((layer) => {
      this.map.NativeMap.addLayer(layer);
      this.createReferences(layer);
    });
    this.createInteractions();
  }

  createOverlayLayersAndControls(overlays:IMapLayer[]) {
    if (!(overlays && overlays.length>0)) {
      return; // do nothing
    }
    // create overlay layers and internal vector layers
    // this.map.NativeMap should have already created, such as in MapUI.mounted()
    //this.getLayersForUser(user);
    let existingVector = null;
    if (this.map.VectorLayers) {
      existingVector = this.map.VectorLayers.map((v) => {
        return v;
      });
    }
    //this.map.VectorLayers = null;
    this.map.MapLayers = overlays;
    this.createOverlayLayers(this.map.MapLayers);
    if (this.map.VectorLayers) {
      this.createInteractions();
    }

    let layers:any[] = this.map.TileLayers || [];
    if (this.map.VectorLayers) {
      layers = layers.concat(this.map.VectorLayers);
    }
    layers.forEach((layer) => {
      if (existingVector.indexOf(layer)==-1) {
        this.map.NativeMap.addLayer(layer);
        this.createReferences(layer);
      }
    });
  }

  createAPoint(lonLat:Coordinate) : Point {
    return new Point(this.lonLatToMap(lonLat));
  }

  /**
   * called from MAPUI.beforeDestroy()
   */
  clearOverlayLayersAndControls() {
    if (this.map.MapLayers) {
      this.map.MapLayers.forEach((ml) => {
        if (ml.mapSources) {
          ml.mapSources.forEach((ms) => {
            delete ms.mapLayers;
            delete ms.nativeLayer;
          });
        }
      });
    }
  }

  UndoRedo: UndoRedo;
  createUndoRedo(undoRedo:IUndoRedo) {
    this.UndoRedo = new UndoRedo(undoRedo);
  }

  addUndo(feature:Feature, action:"Insert" | "Update" | "Delete", append?:boolean) {
    if (this.UndoRedo) {
      this.UndoRedo.addUndo(feature, action, append);
    }
  }

  clearUndoRedo() {
    if (this.UndoRedo) {
      this.UndoRedo.clear();
    }
  }

  createFilter(filter:{[key:string]:string}) {
    this.map.Filter = Object.assign({},this.map.Filter || {}, filter);
    //this.map.Filter = filter;
    if (this.map.MapLayers && this.map.MapLayers.length>0) {
      this.map.MapLayers.forEach((ml) => {
        if (ml.mapSources && ml.mapSources.length>0) {
          ml.mapSources.forEach((ms) => {
            let filter = this.map.Filter;
            let source;
            if (ms.filterOptions && ms.nativeLayer) {
              let cqls:string[] = [];
              for (let fo in filter) {
                if (ms.filterOptions.indexOf(fo)>=0) {
                  cqls.push(filter[fo]);
                }
              }
              source = ms.nativeLayer.getSource();
              if (cqls.length>0) {
                let cql = cqls.join(' AND ');
                if (ms.params && ms.params.CQL_FILTER) {
                  cql += ' AND ' + ms.params.CQL_FILTER;
                }
                if (ms.service.match(/wms/i)) {
                  let params = source.getParams() || {};
                  params = Object.assign({},params,{CQL_FILTER:cql});
                  source.updateParams(params);
                } else if (ms.service.match(/wfs/i)) {
                  ms['CQL_FILTER'] = cql;
                }
              }
            }
          });
        }
      });
    }
  }

  clearFilter() {
    if (this.map.Filter) {
      delete this.map.Filter;
      if (this.map.MapLayers && this.map.MapLayers.length>0) {
        this.map.MapLayers.forEach((ml) => {
          if (ml.mapSources && ml.mapSources.length>0) {
            ml.mapSources.forEach((ms) => {
              if (ms.nativeLayer) {
                let source = ms.nativeLayer.getSource();
                if (ms.service.match(/wms/i)) {
                  let params = source['getParams']() || {};
                  if (params['CQL_FILTER']) {
                    delete params['CQL_FILTER'];
                    source['updateParams'](Object.assign({},params));
                  }
                } else if (ms.service.match(/wfs/i)) {
                  // todo
                }
              }
            });
          }
        });
      }
    }
  };

  // trigger from this.map.Map.onMoveEnd()
  syncBaseMapViews(center, zoom, rotation) {
    BASEMAP.forEach((provider) => {
      provider.Maps.forEach((map) => {
        if (map.NativeMap) {
          let v = map.NativeMap.getView();
          v.setCenter(center);
          v.setZoom(zoom);
          v.setRotation(rotation);
        }
      });
    }, this);
  };

  changeBaseMap(basemap:string) {
    let vInfo:IMapViewInfo = this.getViewInfo();
    if (basemap=='gistda.thaichote' && vInfo.zoom>=16) {
      basemap = this.map.HasGoogleMap ? 'google.satellite' : 'bing.aerial';
    }
    let BM = this.getBaseMap(basemap);
    if (this.map.BaseMap != BM.Layer) {
      let oldBaseMapType = this.map.BaseMapType;
      this.map.BaseMapType = BM.IsImage ? 'image' : 'map';
      this.map.BaseMap = BM.Layer;
      let layers = this.map.NativeMap.getLayers();
      layers.removeAt(0);
      setTimeout(() => {
        layers.insertAt(0, BM.Layer);
        if (oldBaseMapType!=this.map.BaseMapType) {
          let refreshTheseLayers = [this.map.AoiLayer];
          let editLayers = this.getEditingLayers();
          if (!!editLayers) {
            refreshTheseLayers = refreshTheseLayers.concat(
              [...editLayers.Point,...editLayers.Line,...editLayers.Polygon]
            );
          }
          refreshTheseLayers.forEach((layer) => {
            if (layer) {
              layer.getSource().refresh();
            }
          });
        }
        //todo: not working
        this.activateGoogleMaps(basemap);
      }, 0);
    }
  };

  activateGoogleMaps(basemap:string) {
    try {
      if (this.map.BaseMap instanceof GoogleLayer) {
        let bm = this.getBaseMap(basemap);
        let mt = this.getGoogleMapTypeId(bm);
        if (!this.map.GoogleMap) {
          this.map.GoogleMap = new OLGoogleMaps({
            map: this.map.NativeMap,
            watch: {
              tile: false,
              image: false,
              vector: false
            },
            gmapOptions: {
              mapTypeId: mt
            }
          });
        } else {
          let gmap = this.map.GoogleMap.getGoogleMapsMap();
          gmap.setMapTypeId(mt);
        }
        this.map.GoogleMap.activate();
      } else if (!!this.map.GoogleMap) {
        this.map.GoogleMap.deactivate();
      }
    } catch (error) {
      console.log('Error in activateOlGoogleMaps (Map.ts): ');
      console.log(error);
    }
  }

  activateGoogleMapsX(basemap:string) {
    try {
      if (this.map.BaseMap instanceof GoogleLayer) {
        let bm = this.getBaseMap(basemap);
        let mt = this.getGoogleMapTypeId(bm);
        this.map.BaseMap['setMapTypeId'](mt);
        this.map.BaseMap.olMap_ = this.map.NativeMap;
      }
    } catch (error) {
      console.log('Error in activateOlGoogleMaps (Map.ts): ');
      console.log(error);
    }
  }

  getBaseMaps() {
    return BASEMAP;
  };

  getBaseMap(basemap:string) {
    basemap = (basemap || DEFAULT_BASEMAP).toLowerCase();
    let bm;
    BASEMAP.forEach((provider) => {
      provider.Maps.forEach((map) => {
        if (basemap==map.Id) {
          bm = map;
          return;
        }
      });
    }, this);
    return bm;
  };

  set Scale(scale:number) {
    let view = this.map.NativeMap.getView();
    let proj = view.getProjection();
    let res = scale / (this.map.XDPI * INCHES_PER_METER) / proj.getMetersPerUnit();
    view.setResolution(res);
  }

  get Scale() : number {
    let view = this.map.NativeMap.getView();
    let resolution = view.getResolution();
    let projection = view.getProjection();
    let pointResolution = projection.getMetersPerUnit() * resolution;
    let scale = Math.round(pointResolution * this.map.XDPI * INCHES_PER_METER);
    return scale;
  }

  getNormalizedScale() : number {
    let scale = this.Scale;
    if (scale<=500) {
      scale = 500;
    } else if (scale<=1000) {
      scale = 1000;
    } else if (scale<10000) {
      scale = Math.round(scale/100) * 100;
    } else if (scale<100000) {
      scale = Math.round(scale/1000) * 1000;
    } else if (scale<1000000) {
      scale = Math.round(scale/10000) * 10000;
    } else {
      scale = Math.round(scale/100000) * 100000;
    }
    //this.Scale = scale;
    return scale;
  }

  getZoom() : number {
    let view = this.map.NativeMap.getView();
    return view.getZoom();
  };

  get Projection() : Projection {
    let view = this.map.NativeMap.getView();
    return view.getProjection();
  }

  isPointGeometry(geometry:Geometry) : boolean {
    return geometry instanceof Point;
  }

  isLineGeometry(geometry:Geometry) : boolean {
    return geometry instanceof LineString;
  }
  
  isPolygonGeometry(geometry:Geometry) : boolean {
    return geometry instanceof Polygon;
  }

  getZoomForResolution(resolution:number) : number {
    let view = this.map.NativeMap.getView();
    return view.getZoomForResolution(resolution);
  };

  getMetersPerPixel() : number {
    let ext = this.map.NativeMap.getView().calculateExtent();
    let mapSize = this.map.NativeMap.getSize();
    return (ext[2] - ext[0]) / mapSize[0];
  }

  XgetViewExtent() : IMapViewExtent {
    let size = this.map.NativeMap.getSize();
    let minXY = this.map.NativeMap.getCoordinateFromPixel([0,size[1]]);
    let maxXY = this.map.NativeMap.getCoordinateFromPixel([size[0],0]);
    let ext:[number,number,number,number] = [
      minXY[0], minXY[1],maxXY[0],maxXY[1]
    ];
    let view = this.map.NativeMap.getView();
    let Xext = view.calculateExtent(this.map.NativeMap.getSize());
    let wgs84LF = proj.toLonLat([ext[0],ext[1]]);
    let wgs84UR = proj.toLonLat([ext[2],ext[3]]);
    let center = view.getCenter();
    let lonLat = proj.toLonLat(center);
    let utm = UTM.lonLatToUTM(lonLat[0], lonLat[1]);
    let utmLF = UTM.lonLatToUTM(wgs84LF[0], wgs84LF[1], utm.zone);
    let utmUR = UTM.lonLatToUTM(wgs84UR[0], wgs84UR[1], utm.zone);
    return {
      map: [ext[0], ext[1], ext[2], ext[3]],
      wgs84: [wgs84LF[0], wgs84LF[1], wgs84UR[0], wgs84UR[1]],
      utm: [utmLF.x, utmLF.y, utmUR.x, utmUR.y]
    };
  };

  getViewExtent() : IMapViewExtent {
    let view = this.map.NativeMap.getView();
    let ext = view.calculateExtent(this.map.NativeMap.getSize());
    let wgs84LF = proj.toLonLat([ext[0],ext[1]]);
    let wgs84UR = proj.toLonLat([ext[2],ext[3]]);
    let center = view.getCenter();
    let lonLat = proj.toLonLat(center);
    let utm = UTM.lonLatToUTM(lonLat[0], lonLat[1]);
    let utmLF = UTM.lonLatToUTM(wgs84LF[0], wgs84LF[1], utm.zone);
    let utmUR = UTM.lonLatToUTM(wgs84UR[0], wgs84UR[1], utm.zone);
    return {
      map: [ext[0], ext[1], ext[2], ext[3]],
      wgs84: [wgs84LF[0], wgs84LF[1], wgs84UR[0], wgs84UR[1]],
      utm: [utmLF.x, utmLF.y, utmUR.x, utmUR.y]
    };
  };

  getViewInfo() : IMapViewInfo {
    let view = this.map.NativeMap.getView();
    let center = view.getCenter();
    let lonLat = proj.toLonLat(center);
    let utm = UTM.lonLatToUTM(lonLat[0], lonLat[1]);
    return {
      size: <number[]> this.map.NativeMap.getSize(),
      zoom: view.getZoom(),
      rotation: view.getRotation(),
      center: {
        map: [center[0], center[1]],
        wgs84: [lonLat[0], lonLat[1]],
        utm: utm
      },
      paddingCenter: this.getPaddingCenterCoordinate(),
      extent: this.getViewExtent()
    };
  };

  getPixelFromLonLat(lonLat:Coordinate) : [number,number] {
    let coord = proj.fromLonLat(lonLat);
    let pixel = this.map.NativeMap.getPixelFromCoordinate(coord);
    return [pixel[0], pixel[1]];
  }

  getPaddingCenterPixel() : Coordinate {
    let ms = this.map.NativeMap.getSize();
    return [
      (ms[0] + this.viewProps.padding[3] - this.viewProps.padding[1]) / 2,
      (ms[1] + this.viewProps.padding[0] - this.viewProps.padding[2]) / 2
    ];
  };

  getPaddingCenterCoordinate() : any {
    let pixel = this.getPaddingCenterPixel();
    let map = this.map.NativeMap.getCoordinateFromPixel(pixel);
    let wgs84 = proj.toLonLat(map);
    return {
      pixel: pixel,
      map: map,
      wgs84: wgs84,
      utm: UTM.lonLatToUTM(wgs84[0], wgs84[1])
    };
  };

  getMapCenterForCoordinateAt(zoom?:number, pixel?:Coordinate, lonlat?:Coordinate) : any {
    let view = this.map.NativeMap.getView();
    pixel = pixel || this.getPaddingCenterPixel();
    zoom = (zoom || zoom===0) ? zoom : view.getZoom();
    let coord;
    let isEPSG4326: boolean = false;
    if (!!lonlat) {
      if (lonlat[0]>=-180 && lonlat[0]<=180 && 
        lonlat[1]>=-90 && lonlat[1]<=90) {
        isEPSG4326 = true;
        coord = proj.fromLonLat(lonlat);
      } else {
        coord = lonlat;
      }
    } else {
      coord = this.map.NativeMap.getCoordinateFromPixel(pixel);
    }
    let size = this.map.NativeMap.getSize();
    let rotation = view.getRotation();
    let cosAngle = Math.cos(-rotation);
    let sinAngle = Math.sin(-rotation);
    let rotX = coord[0] * cosAngle - coord[1] * sinAngle;
    let rotY = coord[1] * cosAngle + coord[0] * sinAngle;
    let resolution = view.getResolutionForZoom(zoom);
    rotX += (size[0] / 2 - pixel[0]) * resolution;
    rotY += (pixel[1] - size[1] / 2) * resolution;
  
    // go back to original angle
    sinAngle = -sinAngle; // go back to original rotation
    let centerX = rotX * cosAngle - rotY * sinAngle;
    let centerY = rotY * cosAngle + rotX * sinAngle;
    let wgs84 = isEPSG4326 ? lonlat : proj.toLonLat([centerX, centerY]);
    return {
      map: [centerX, centerY],
      wgs84: wgs84,
      utm: UTM.lonLatToUTM(wgs84[0], wgs84[1])
    };
  };
  
  MousePosition : {
    wgs84: number[];
    map: number[];
  } = null;
  mousePositionToWGS84(coordinate) {
    this.MousePosition = {
      wgs84: coordinate,
      map: this.lonLatToMap(coordinate)
    };
    return 'WGS84:' + coordinate[0].toFixed(6) + ',' + coordinate[1].toFixed(6);
  };

  mousePositionToUTM(coordinate) {
    let utm = UTM.lonLatToUTM(coordinate[0],coordinate[1]);
    return 'UTM' + utm.zone + ':' + utm.x.toFixed(2) + ',' + utm.y.toFixed(2);
  };

  degreeToDMS(degree) : IDMS {
    let d = Math.floor(degree);
    let s = (degree - d) * 60;
    let m = Math.floor(s);
    s = Math.round((s - m) * 60);
    if (m==60) {
      d++;
      m = 0;
    }
    if (s==60) {
      m++;
      s = 0;
    }
    return {
      degree: d,
      minute: m,
      second: s
    };
  }
  
  get MapExtent() : [number,number,number,number] {
    return this.viewProps.extent;
  }
  
  set MapExtent(extent:[number,number,number,number]) {
    if (!(extent && extent.length==4)) {
      extent = this.viewProps.extent;
    }
    let extent4326,extent3857;
    let minxy:number[], maxxy:number[];
    if (extent[0]>=-180 && extent[0]<=180 && extent[1]>=-90 && extent[1]<=90) {
      // this should be EPSG:4326
      extent4326 = extent;
      minxy = proj.transform([extent[0], extent[1]], 'EPSG:4326', 'EPSG:3857');
      maxxy = proj.transform([extent[2], extent[3]], 'EPSG:4326', 'EPSG:3857');
      extent3857 = [minxy[0],minxy[1],maxxy[0],maxxy[1]];
    } else {
      // this should be EPSG:3857
      extent3857 = extent;
      minxy = proj.transform([extent[0], extent[1]], 'EPSG:3857', 'EPSG:4326');
      maxxy = proj.transform([extent[2], extent[3]], 'EPSG:3857', 'EPSG:4326');
      extent4326 = [minxy[0],minxy[1],maxxy[0],maxxy[1]];
    }
    if (this.map.MapLayers) {
      this.map.MapLayers.forEach((ml) => {
        if (ml.mapSources && ml.mapSources.length>0) {
          ml.mapSources.forEach((ms) => {
            if (ms.nativeLayer && ms.filterOptions) {
              ms.nativeLayer.setExtent(extent3857);
            }
          });
        }
      });
    }
    // do following after ms.nativeLayer.setExtent(extent3857);
    this.viewProps.extent = extent4326;
    this.changeMapView('zoomToExtent', {extent:extent3857});
  };

  get ViewState() : IMapViewProps {
    return this.viewProps;
  }

  get ViewPadding() : number[]{
    let offset = [64,80,64,80]; //???
    return this.viewProps.padding.map((p,index) => {
      return p + offset[index];
    });
  }

  private isChangingPadding:boolean = false;
  set ViewPadding(padding:number[]) {
    this.viewProps.padding = padding;
    /*if (this.isChangingPadding || !(this.map && this.map.NativeMap)) {
      this.isChangingPadding = false;
      return;
    }
    this.isChangingPadding = true;
    setTimeout(() => { // IMPORTANT
      this.changeMapView('zoomToExtent');
      this.isChangingPadding = false;
    },0);*/
  }

  /**
   * depreciated.
  set ViewPaddingX(padding:number[]) {
    let op = this.viewProps.padding.map((p) => {
      return p;
    });
    this.viewProps.padding = padding;
    if (this.isChangingPadding) {
      this.isChangingPadding = false;
      return;
    }
    this.isChangingPadding = true;
    setTimeout(() => { // IMPORTANT
      let dp = this.viewProps.padding.map((p,index) => {
        if (index<=1) {
          return p - op[index];
        } else {
          return op[index] - p;
        }
      });
      let ms = this.map.NativeMap.getSize();
      let ve = this.map.NativeMap.getView().calculateExtent();
      let mppx = (ve[2] - ve[0]) / ms[0]; // meter per pixel in x
      let mppy = (ve[3] - ve[1]) / ms[1]; // meter per pixel in y
      ve = [
        ve[0] + dp[3] * mppx,
        ve[1] + dp[2] * mppy,
        ve[2] + dp[1] * mppx,
        ve[3] + dp[0] * mppy
      ];
      let center = [
        (ve[0] + ve[2]) / 2,
        (ve[1] + ve[3]) / 2
      ];
      this.changeMapView('panTo',{center:center});
      this.isChangingPadding = false;
    },0);
  }*/

  onViewAnimationEnd(view,state) {
    //state: true: completed, false: cancelled
    if (this.map.BaseMap instanceof GoogleLayer && !!this.map.GoogleMap.gmap) {
      let zoom = this.map.GoogleMap.gmap.getZoom();
      view.setZoom(zoom);
    }
  }

  changeMapView(cmd:string, params?:any) {
    this.clearHighlightFeature();
    params = params || {};
    let view = this.map.NativeMap.getView();
    if (cmd=='zoomToExtent') {
      let extent = params.extent || this.viewProps.extent;
      view.setRotation(0);
      if (extent) {
        let minxy:number[], maxxy:number[];
        if (extent[0]>=-180 && extent[0]<=180 && extent[1]>=-90 && extent[1]<=90) {
          // this should be EPSG:4326
          minxy = proj.transform([extent[0], extent[1]], 'EPSG:4326', 'EPSG:3857');
          maxxy = proj.transform([extent[2], extent[3]], 'EPSG:4326', 'EPSG:3857');
          extent = [minxy[0],minxy[1],maxxy[0],maxxy[1]];
        }
        //view['isAnimating'] = true;
        let self = this;
        view.fit(extent, {
          padding: this.ViewPadding,
          duration: MV_ANIMATION_DURATION,
          callback: (state) => {
            self.onViewAnimationEnd(view, state);
          },
          constrainResolution: false // nearest: true
        });
      } else if (this.viewProps.center && this.viewProps.zoom) {
        this.changeMapView('setCenterAt', {center: this.viewProps.center});
        let self = this;
        view.animate({
          zoom: this.viewProps.zoom, 
          duration: MV_ANIMATION_DURATION
        },(state) => {
          self.onViewAnimationEnd(view, state);
        });
      }
    } else if (cmd=='setCenterAt') {
      let zoom = params.zoom || view.getZoom();
      let mc = this.getMapCenterForCoordinateAt(zoom, params.pixel, params.center);
      let self = this;
      view.animate({
        center: mc.map,
        zoom: zoom, 
        duration: MV_ANIMATION_DURATION
      },(state) => {
        self.onViewAnimationEnd(view, state);
      });
    } else if (cmd == 'pan') {
      let dx = params.dx || 0;
      let dy = params.dy || 0;
      if (dx==0 && dy==0) return;
      let vc = view.getCenter();
      let ext = view.calculateExtent(this.map.NativeMap.getSize());
      let xc = vc[0] + (ext[2] - ext[0]) * dx;
      let yc = vc[1] + (ext[3] - ext[1]) * dy;
      let self = this;
      view.animate({
        center: [xc,yc], 
        duration: MV_ANIMATION_DURATION
      },(state) => {
        self.onViewAnimationEnd(view, state);
      });
    } else if (cmd =='panTo') {
      if (params && params.center) {
        this.changeMapView('setCenterAt', {
          center: params.center
        });
      }
    } else if (cmd=='zoom') {
      let dz = params.dz || 0;
      if (dz!=0) {
        let zoom = view.getZoom() + dz;
        let mc = this.getMapCenterForCoordinateAt(zoom);
        //let mc = this.getPaddingCenterCoordinate();
        let self = this;
        view.animate({
          center: mc.map,
          zoom: zoom, 
          duration: MV_ANIMATION_DURATION
        },(state) => {
          self.onViewAnimationEnd(view, state);
        });
      }
    }
  };

  createInteractions() {
    if (!this.map.HighlightFeatureTool) {
      this.map.HighlightFeatureTool = new Select({
        layers: (layer) => {
          //excluding these layers
          let map = this.map;
          if (!map.Action.name || layer==this.map.AoiLayer || 
              layer==this.map.DrawingLayer || 
              (layer['SOFTOO_SOURCE'] && layer['SOFTOO_SOURCE'].canHover===false)) {
            return false;
          }
          if (map.Action.isEditingTool) {
            return layer['SOFTOO_SOURCE'] && layer['SOFTOO_SOURCE'].canEdit;
          } else {
            return map.VectorLayers.indexOf(<LayerVector>layer)>=0;
          }
        },
        multi: false, //select only one feature at a time
        condition: condition.pointerMove,
        //toggleCondition: condition.never,
        style: STYLE.Highlight
      });
      this.map.HighlightFeatureTool.on('select',(e)=> {
        let sfs = e.deselected;
        if (sfs && sfs.length>0) {
          sfs.forEach((f) => {
            if (f.get('STO_FLASH_PIN')=='SOFTOO_LOCATOR') {
              f.setStyle(STYLE.FlagBlue);
            } else {
              f.setStyle(null);
            }
            //f.set('IS_HIGHLIGHTED__', false);
          });
        }
        sfs = e.selected;
        this.map.HighlightedFeature = (sfs && sfs.length>0) ? sfs[0] : null;
        if (this.map.HighlightedFeature) {
          let action = this.map.Action ? this.map.Action.name + '' : '';
          let hoverInfo = this.getFeatureHoverInfo(this.map.HighlightedFeature);
          if (!!hoverInfo || !!action.match(/getInfo|cutPolygonHole|splitPolygon|scalePolygonByArea|transformFeature|modifyFeature|moveFeature|translateFeature|indian75ToWgs84|deleteFeature/i)) {
            let cursor = this.map.HighlightedFeature ? 'pointer' : '';
            //this.map.NativeMap.getViewport().style.cursor = cursor;
            this.map.NativeMap.getTargetElement().style.cursor = cursor;
            if (!!hoverInfo) {
              let geom = this.map.HighlightedFeature.getGeometry();
              let options:IOverlay = {};
              if (geom instanceof Point) {
                options.position = geom.getCoordinates();
              }
              this.showFeatureHoverInfo(hoverInfo, options);
            } else if (this.map.HoverInfo) {
              this.map.NativeMap.removeOverlay(this.map.HoverInfo);
            }
          }
        } else {
          if (this.map.HoverInfo) {
            this.map.NativeMap.removeOverlay(this.map.HoverInfo);
          }
          //this.map.NativeMap.getViewport().style.cursor = '';
          this.map.NativeMap.getTargetElement().style.cursor = '';
        }
      });
      this.map.NativeMap.addInteraction(this.map.HighlightFeatureTool);
    }
    this.createSnap();
  };

  /**
   * All Snap interactions must be added after others, such as Modify and Draw,
   * in order for the Snaps map browser event handlers to be fired first. 
   * Otherwise, Modify and draw wont get Snap working;
   * @param interactions
   */
  addInteractions(interactions:any[]) {
    let existings: Collection<Interaction> = this.map.NativeMap.getInteractions();
    let insertAt:number = -1;
    let n = existings.getLength();
    for (let i=0; i<n; i++) {
      if (existings.item(i) instanceof Snap) {
        insertAt = i;
        break;
      }
    }
    if (insertAt==-1) {
      interactions.forEach((i) => {
        this.map.NativeMap.addInteraction(i);
      });
    } else {
      interactions.forEach((i) => {
        existings.insertAt(insertAt, i);
        insertAt++;
      });
    }
  }

  createSnap() {
    //create Snap interaction for every vector layer.
    // The snap interaction must be added after the Modify and Draw interactions
    // in order for its map browser event handlers to be fired first. Its handlers
    // are responsible of doing the snapping.
    let interactions = this.map.NativeMap.getInteractions();
    interactions.forEach((i) => {
      if (i instanceof Snap) {
        this.map.NativeMap.removeInteraction(i);
      }
    });
    let excludings = [
      this.map.SearchFeatureLayer,
      this.map.AoiLayer,
      this.map.SelectedFeatureLayer
    ];
    this.map.VectorLayers.forEach((v) => {
      let snap = new Snap({
        source: v.getSource(),
        pixelTolerance: PIXEL_TOLERANCE
      });
      this.map.NativeMap.addInteraction(snap);
    });
  }
  
  setMapAction(action:string, params?:{[key:string] : any}) :string {
    this.clearHighlightFeature();
    params = params || {};
    if (!action || (
      this.map.Action.name==action && Object.is(params, this.map.Action.params))) {
      // the same tool
      this.removeActiveTool(); //To toggle map tool
      return null;
    }
    if (action=='measure') {
      this.measure(<IMeasurer> params);
    } else if (action=='getInfo' || action=='linkData') {
      this.removeMeasurer();
      this.clearLayer('SelectedFeatureLayer');
      this.clearLayer('SearchFeatureLayer');
      this.getInfo(action, params);
    } else if (action=='undo') {
      this.UndoRedo && this.UndoRedo.undo();
    } else if (action=='redo') {
      this.UndoRedo && this.UndoRedo.redo();
    } else {
      this.removeActiveTool();
      let tool, options, isEditingTool = false;
      let self = this;
      let editLayers = this.getEditingLayers();
      let editLayer;
      switch (action) {
      case 'addPoint':
        editLayer = !!editLayers.Point ? editLayers.Point[0] : this.map.DrawingLayer;
        options = Object.assign({
          type: 'Point',
          clickTolerance: Math.max(PIXEL_TOLERANCE-2, 4),
          style: params.style || STYLE.Measure,
          source: editLayer.getSource(),
          drawingSource: this.map.DrawingLayer.getSource()
        }, params);
        tool = new Drawer(options);
        break;
      case 'addPointXY':
        break;
      case 'addPointAzimuthDistance':
        break;
      case 'addPointBearingDistance':
        break;
      case 'addPointDxDy':
        break;
      case 'addPointA2BDistance':
        break;
      case 'addPolygon':
        editLayer = !!editLayers.Polygon ? editLayers.Polygon[0] : this.map.DrawingLayer;
        options = Object.assign({
          type: 'Polygon',
          clickTolerance: Math.max(PIXEL_TOLERANCE-2, 4),
          style: params.style || STYLE.Measure,
          source: editLayer.getSource(),
          drawingSource: this.map.DrawingLayer.getSource()
        }, params);
        tool = new Drawer(options);
        break;
      case 'createConvexHull':
        editLayer = !!editLayers.Polygon ? editLayers.Polygon[0] : this.map.DrawingLayer;
        options = Object.assign({
          layers: editLayers.Point, // where input points are from
          style: this.map.SelectedFeatureLayer.getStyle(),
          polygonSource: editLayer.getSource(), // where convexhull is stored to
          drawStyle: STYLE.Measure,
          drawSource: this.map.DrawingLayer.getSource(),
          pixelTolerance:PIXEL_TOLERANCE
        }, params);
        tool = new ConvexHull(options);
        break;
      case 'cutPolygonHole':
        options = Object.assign({
          type: 'Polygon',
          clickTolerance: Math.max(PIXEL_TOLERANCE-2, 4),
          style: STYLE.Measure,
          drawingSource: this.map.DrawingLayer.getSource()
        }, params);
        tool = new CutHole(options);
        isEditingTool = true;
        break;
      case 'splitPolygon':
        options = Object.assign({
          type: 'LineString',
          clickTolerance: Math.max(PIXEL_TOLERANCE-2, 4),
          style: STYLE.Measure,
          drawingSource: this.map.DrawingLayer.getSource()
        }, params);
        tool = new Splitter(options);
        isEditingTool = true;
        break;
      case 'scalePolygonByArea':
        break;
      case 'transformFeature':
        break;
      case 'modifyFeature':
        options = Object.assign({
          layers: [...editLayers.Point,...editLayers.Line,...editLayers.Polygon], // selected features in these layers will be modified
          selectStyle: this.map.SelectedFeatureLayer.getStyle(),
          style: STYLE.Measure,
          clickTolerance: Math.max(PIXEL_TOLERANCE-2, 4),
          pixelTolerance: PIXEL_TOLERANCE, // must be the same as for Snap
          drawingSource: this.map.DrawingLayer.getSource()
        }, params);
        tool = new ModifyFeature(options);
        isEditingTool = true;
        break;
      case 'moveFeature':
        options = Object.assign({
          layers: [...editLayers.Point,...editLayers.Line,...editLayers.Polygon], // selected features in these layers will be modified
          //selectStyle: this.map.SelectedFeatureLayer.getStyle(),
          clickTolerance: Math.max(PIXEL_TOLERANCE-2, 4),
          style: STYLE.Measure,
          drawingSource: this.map.DrawingLayer.getSource()
        }, params);
        tool = new MoveFeature(options);
        isEditingTool = true;
        break;
      case 'translateFeature':
        options = Object.assign({
          layers: [...editLayers.Point,...editLayers.Line,...editLayers.Polygon], // highlighted features in these layers will be translated
          hitTolerance: Math.max(PIXEL_TOLERANCE-2, 4)
        }, params);
        tool = new TranslateFeature(options);
        isEditingTool = true;
        break;
      case 'indian75ToWgs84':
        options = Object.assign({
          layers: [...editLayers.Point,...editLayers.Line,...editLayers.Polygon], // selected feature in these layers will be moved
          style: this.map.SelectedFeatureLayer.getStyle()
        }, params);
        tool = new Indian75ToWgs84(options);
        isEditingTool = true;
        break;
      case 'deleteFeature':
        options = Object.assign({
          layers: [...editLayers.Point,...editLayers.Line,...editLayers.Polygon], // selected feature in these layers will be deleted
          style: this.map.SelectedFeatureLayer.getStyle()
        }, params);
        tool = new DeleteFeature(options);
        isEditingTool = true;
        break;
      case 'deleteFeatures':
        options = Object.assign({
          layers: [...editLayers.Point,...editLayers.Line,...editLayers.Polygon], // selected features in these layers will be deleted
          style: this.map.SelectedFeatureLayer.getStyle(),
          pixelTolerance: PIXEL_TOLERANCE,
          inPolygon: true,
          polygonStyle: STYLE.Measure,
          polygonSource: this.map.DrawingLayer.getSource()
        }, params);
        tool = new DeleteFeature(options);
        isEditingTool = true;
        break;
      }
      if (tool) {
        this.map.Action.name = action;
        this.map.Action.params = params;
        this.map.Action.tool = tool;
        this.map.Action.isEditingTool = isEditingTool;
        
        this.map.NativeMap.addInteraction(tool);
      }
    }
    return this.map.Action.name;
  }

  removeFeatureFromEditing(feature:string | Feature, type:"Point" | "Line" | "Polygon") {
    let editLayers = this.getEditingLayers()[type];
    if (!(feature instanceof Feature)) {
      let fid = <string> feature;
      let gotIt = false;
      let source, features;
      for (let e=0; e<editLayers.length && !gotIt; e++) {
        source = editLayers[e].getSource();
        features = source.getFeatures();
        for (let i=0; i<features.length && !gotIt; i++) {
          let id = features[i].get('gid');
          if (!id) {
            id = features[i].getId();
            if (id) {
              id = id.split('.')[1];
            }
          } else {
            id += '';
          }
          if (id==fid) {
            feature = features[i];
            gotIt = true;
          }
        }
      }
    }
    if (feature instanceof Feature) {
      let state = feature.get('STATE');
      feature['NATIVE_SOURCE'].removeFeature(feature);
      this.UndoRedo.removeFeature(feature, state);
    }
  }

  updateSavedFeature(feature:Feature, silent?:boolean) {
    let source = feature['NATIVE_SOURCE'];
    if (!source) return;
    let gid = feature.get('gid');
    let state = feature.get('STATE') || (!gid ? 'Insert' : 'Update');
    let action:"Insert" | "Update" | "Delete";
    action = state.match(/Delete|Dirty/i) ? 'Delete' : 
             state.match(/Update/i) ? 'Update' : 'Insert';
    
    if (action=='Delete') {
      this.UndoRedo.removeFeature(feature,action);
      source.removeFeature(feature);
    } else {
      let p, newProps = feature.get('newProps');
      if (!!newProps) {
      /*  throw {
          id: 'bad_data',
          status: 400,
          message: 'No required data returned from saved feature'
        };
      } else {*/
        this.UndoRedo.removeFeature(feature,action); // do this before run feature.setId()
        feature.unset('newProps');
        if (newProps.hasOwnProperty('keyname')) {
          delete newProps.keyname;
        }
        for (p in newProps) {
          if (p=='gid' && !feature.getId()) {
            let ft = source['SOFTOO_SOURCE'].featureType;
            if (ft && ft.indexOf(':')>0) {
              ft = ft.split(':')[1];
              feature.setId(ft + '.' + newProps[p]);
            }
          }
          feature.set(p, newProps[p]);
        }
      }
      feature.unset('STATE');
      if (feature.get('area_sqm')!==undefined) {
        feature.set('originalAreaSqm', feature.get('area_sqm'));
        feature.unset('AreaModifiedMode');
      }
    }
    //source.refresh(); //don't do this since there maybe unsaved edited features
    // but we can refresh related WMS layers
    if (silent!==true) {
      source['SOFTOO_SOURCE'].mapLayers.forEach((ml) => {
        if (ml.mapSources) {
          ml.mapSources.forEach((ms) => {
            if (!ms.service.match(/WFS|Vector/i) &&!!ms.nativeLayer) {
              let s = ms.nativeLayer.getSource();
              if (s.updateParams) {
                s.updateParams({"ts": Date.now()});
              }
              s.refresh();
            }
          });
        }
      });
    }
  }

  updateFeaturesToSave(save:IFeatureToSave) {
    let msSources = [];
    ['points','lines','polygons']
    .forEach((type) => {
      if (!!save[type]) {
        //let hasEditingFeature = false;
        ['dirty','insert','update','delete']
        .forEach((state) => {
          let features = save[type][state.toLowerCase()];
          if (!!features && features.length>0) {
            //hasEditingFeature = true;
            features.forEach((feature) => {
              let source = feature['NATIVE_SOURCE'];
              if (!!source['SOFTOO_SOURCE'] && 
                msSources.indexOf(source['SOFTOO_SOURCE'])==-1) {
                msSources.push(source['SOFTOO_SOURCE']);
              }
              this.updateSavedFeature(feature, true);
            });
          }
        });
      }
    });
    msSources.forEach((ms) => {
      if (!!ms.mapLayers) {
        ms.mapLayers.forEach((ml) => {
          if (ml.mapSources) {
            ml.mapSources.forEach((ms) => {
              if (!ms.service.match(/WFS|Vector/i) && !!ms.nativeLayer) {
                let s = ms.nativeLayer.getSource();
                if (s.updateParams) {
                  s.updateParams({"ts": Date.now()});
                }
                s.refresh();
              }
            });
          }
        });
      }
    });
  }

  getEditingLayers() : ILayerForEdit {
    let layer:ILayerForEdit = {
      Point: [],
      Line: [],
      Polygon: []
    };
    this.map.VectorLayers.forEach((v) => {
      if (v['SOFTOO_SOURCE'] && v['SOFTOO_SOURCE'].canEdit &&
          !!v['SOFTOO_SOURCE'].geometryType) {
        let gt = v['SOFTOO_SOURCE'].geometryType;
        layer[gt].push(v);
      }
    });
    return layer;
  }

  getFeaturesToSave() : IFeatureToSave {
    let save:IFeatureToSave = {
      total: 0
    };
    let editLayers = this.getEditingLayers();
    for (let gt in editLayers) {
      let type = gt.toLowerCase() + 's';
      save[type] = save[type] || {};
      editLayers[gt].forEach((layer) => {
        let features = layer.getSource().getFeatures();
        features.forEach((feature) => {
          let state = feature.get('STATE');
          if (!!state) { //Insert|Update|Delete
            let fid = feature.getId() || feature.get('gid');
            if (state.match(/Delete/i) && !fid) {
              // deleted newly inserted features. 
              // won't save them to db but will be removed from layer after saving
              save[type].dirty = save[type].dirty || [];
              save[type].dirty.push(feature);
            } else {
              //these will be save to or deleted from db
              state = state.toLowerCase();
              if (state=='insert' && !!feature.get('gid')) {
                state = 'update';
              } 
              save[type][state] = save[type][state] || [];
              save[type][state].push(feature);
              save.total++;
            }
          }
        });
      });
      if (isEmptyObject(save[type])) {
        delete save[type];
      }
    }
    return save;
  }

  async getFeaturesToSaveByIntersect(checker:Function) {
    try {
      let saves:IFeatureToSaveByIntersects = {
        intersected: {
          total: 0
        },
        notIntersected: {
          total: 0
        }
      };
      let editLayers = this.getEditingLayers();
      for (let gt in editLayers) {
        let type = gt.toLowerCase() + 's';
        saves.intersected[type] = saves.intersected[type] || {};
        saves.notIntersected[type] = saves.notIntersected[type] || {};
        for (let k=0; k<editLayers[gt].length; k++) {
          let layer = editLayers[gt][k];
          let features = layer.getSource().getFeatures();
          for (let f=0; f<features.length; f++) {
            let feature = features[f];
            let state = feature.get('STATE');
            if (state) { //Insert|Update|Delete
              let fid = feature.getId();
              if (state.match(/Delete/i) && !fid) {
                // deleted newly inserted features. 
                // won't save them to db but will be removed from layer after saving
                saves.notIntersected[type].dirty = saves.notIntersected[type].dirty || [];
                saves.notIntersected[type].dirty.push(feature);
              } else {
                if (!fid) {
                  state = 'insert';
                } else {
                  state = state.toLowerCase();
                }
                if (state=='insert' && feature.get('gid')!==undefined) {
                  feature.unset('gid');
                }
                let intersects;
                if (checker && state!='delete') {
                  intersects = await checker(feature, type);
                } else {
                  intersects = {
                    intersected: false
                  };
                }
                if (intersects.intersected) {
                  feature.set('in_conserved', intersects.name);
                  saves.intersected[type][state] = saves.intersected[type][state] || [];
                  saves.intersected[type][state].push(feature);
                  saves.intersected.total++;
                } else {
                  saves.notIntersected[type][state] = saves.notIntersected[type][state] || [];
                  saves.notIntersected[type][state].push(feature);
                  saves.notIntersected.total++;
                }
              }
            }
          };
        }
        if (isEmptyObject(saves.intersected[type])) {
          delete saves.intersected[type];
        }
        if (isEmptyObject(saves.notIntersected[type])) {
          delete saves.notIntersected[type];
        }
      }
      ['intersected','notIntersected'].forEach((p) => {
        if (saves[p].total==0 && 
          !(saves[p].points || saves[p].lines || saves[p].polygons)) {
          delete saves[p];
        }
      });
      return saves;
    } catch (error) {
      throw(error);
    }
  }

  getInfo(action:string, params?:any) {
    let tool = this.map.Action.tool;
    if (tool && tool!=this.map.HighlightFeatureTool) {
      this.removeActiveTool();
    }
    this.map.NativeMap.on('singleclick', this.fetchShortInfo);
    this.map.Action.name = action; //'getInfo';
    this.map.Action.params = params; //this may be ther tolerance
    this.map.Action.tool = null;
    //this.map.Action.tool = this.map.HighlightFeatureTool;
    //this.map.NativeMap.addInteraction(this.map.Action.tool);
  }

  InfoLoaderPosition:string = 'left:0;top:-9999999999px';
  InfoLoaderXY:Coordinate = [0,0];
  fetchShortInfo(event) {
    //show info popup overlay
    let wgs84 = proj.toLonLat(event.coordinate);
    let info:IFeatureInfo = {
      isCluster: false,
      feature: this.map.HighlightedFeature || null,
      location: {
        pixel: event.pixel,
        map: event.coordinate,
        wgs84: [wgs84[0],wgs84[1]],
        utm: UTM.lonLatToUTM(wgs84[0], wgs84[1])
      }
    }
    // 16 is the radius of the loader circle
    this.InfoLoaderPosition = 'left:' + (event.pixel[0] - 16) + 'px;' +
                              'top:' + (event.pixel[1] - 16) + 'px';
    this.InfoLoaderXY = event.coordinate;
    this.clearLayer("SelectedFeatureLayer");
    if (info.feature) {
      if (info.feature['NATIVE_SOURCE'] instanceof Cluster) {
        info.isCluster = true;
        info.features = info.feature.getProperties().features;
      } else {
        this.selectFeature(info.feature);
      }
    }
    this.$emit('getinfo', info);
  }

  selectFeature(feature:Feature) {
    let sfSource = this.map.SelectedFeatureLayer.getSource();
    // IMPORTANT to clone the feature since its style will be changed
    let f = feature.clone();
    sfSource.addFeature(f);
    // IMPORTANT: do followings and do them after f has be added to sfSource
    // We also need to maintain its SOFTOO_SOURCE for getInfo()
    f.setId(feature.getId());
    f['NATIVE_SOURCE'] = feature['NATIVE_SOURCE'] || sfSource; 
  }

  measure(measurer?:IMeasurer) {
    if (!measurer) {
      measurer = {
        name: 'distance',
        crs: 'WGS84',
        unit: 'metric'
      }
    }
    let activeTool = this.map.Action.tool;
    let activeMeasurer = this.map.Action.params;

    if (activeTool && (activeTool instanceof Measurer) && activeMeasurer) {
      if (activeMeasurer.Type != measurer.name) {
        activeTool.Type = measurer.name;
      }
      if (activeMeasurer.crs != measurer.crs) {
        activeTool.CRS = measurer.crs;
      }
      if (activeMeasurer.unit != measurer.unit) {
        activeTool.Unit = measurer.unit;
      }
      this.map.Action.name = 'measure';
      this.map.Action.params = measurer;
      return;
    } else {
      this.removeActiveTool();
    }
    if (measurer.name=='distance') {
      measurer.unit = 'metric'; //force to use metric
    }
    let ctrl: Measurer;
    let controls:Array<any> = this.map.NativeMap.getControls().getArray();
    for (let i=0; i<controls.length; i++) {
      ctrl = controls[i];
      if (ctrl instanceof Measurer) {
        break;
      }
      ctrl = null;
    }
    if (ctrl) {
      // this should not be true, but just in case
      ctrl.Type = measurer.name;
      ctrl.Unit = measurer.unit;
      this.map.Action.tool = ctrl;
      //ctrl.remove();
    } else {
      setTimeout(() => {
        ctrl = new Measurer({
          target: measurer.target,
          type: measurer.name,
          source: this.map.DrawingLayer.getSource(),
          style: STYLE.Measure,
          unit: measurer.unit,
          crs: measurer.crs
        });
        this.map.NativeMap.addControl(ctrl);
        this.map.Action.name = 'measure';
        this.map.Action.params = measurer;
        this.map.Action.lastTool = this.map.Action.tool;
        this.map.Action.tool = ctrl;
      }, 0);
    }
  };

  transformGeomToWGS84(geom, epsgTo) {
    geom.transform(this.Projection, epsgTo);
    return geom;
  }

  transformGeomToUTM(geom) {
    // we only support UTM 47&48.
    let extent = geom.getExtent();
    let cx = (extent[0] + extent[2]) / 2.0;
    let cy = (extent[1] + extent[3]) / 2.0;
    let p = proj.toLonLat([cx,cy]);
    let zone = Math.floor((p[0] + 180.0)/6) + 1;
    if (zone<47 || zone>48) {
      return geom
    }
    let epsgUTM;
    if (p[1]>=0) {
      epsgUTM = 'EPSG:326' + zone;
    } else {
      epsgUTM = 'EPSG:327' + zone;
    }
    geom.transform(this.Projection,'EPSG:4326');
    geom.transform('EPSG:4326', epsgUTM);
    return geom;
  }

  calcLength(line:LineString) : number {
    // 3857 -> 4326 -> 32647/48
    let geom = this.transformGeomToUTM(line.clone());
    let length = geom.getLength();
    return length; // Math.round(length * 100) / 100.0;
  }

  calcArea(polygon:Polygon | MultiPolygon) : number {
    // 3857 -> 4326 -> 32647/48
    let geom = this.transformGeomToUTM(polygon.clone());
    let area = geom.getArea();
    return area; // Math.round(area * 100) / 100.0;
  }

  /*
  changeMeasurerUnit(unit:"metric" | "thai") {
    if (this.activeTool && (this.activeTool instanceof Measurement)) {
      this.measurerAreaUnit = this.measurerUnit;
      this.activeTool.Unit = this.measurerUnit;
    }
  }

  changeMeasurerCRS(crs: "WGS84" | "UTM") {
    if (this.activeTool && (this.activeTool instanceof Measurement)) {
      this.activeTool.CRS = this.measurerCRS;
    }
  }*/

  removeActiveTool() {
    this.map.NativeMap.getViewport().style.cursor = '';
    let action = this.map.Action ? this.map.Action.name + '' : '';
    let tool = this.map.Action ? this.map.Action.tool : null;
    if (action.match(/getInfo|linkData/)) {
      this.map.SelectedFeatureLayer.getSource().clear();
      this.map.NativeMap.un('singleclick', this.fetchShortInfo);
    } else if (tool) {
      if (tool instanceof Measurer) {
        tool.remove();
        //this.hideMeasurer = true;
      } else {
        //in case of interaction:
        if (tool instanceof Interaction) {
          /*if (tool instanceof Drawer || tool instanceof Splitter || 
              tool instanceof CutHole || tool instanceof MoveFeature) {*/
          if (tool['remove']) {
            tool['remove']()
          } else {
            this.map.NativeMap.removeInteraction(tool);
          }
        } else if (tool instanceof Control) {
          this.map.NativeMap.removeControl(tool);
        }
      }
    }
    this.map.Action = {
      lastTool : tool //??
    }
  }

  removeMeasurer() {
    let tool = this.map.Action.tool;
    if (tool instanceof Measurer) {
      tool.remove();
    } else if (this.map.NativeMap) {
      let ctrls = this.map.NativeMap.getControls();
      if (ctrls) {
        for (let i=0; i<ctrls.getLength(); i++) {
          let ctrl = ctrls.item(i);
          if (ctrl instanceof Measurer) {
            ctrl.remove();
            break;
          }
        }
      }
    }
  }
  
  cloneFeature(feature:Feature) : Feature {
    //let p, props = feature.getProperties();
    let f = feature.clone();
    //for (p in props) {
    //  f.set(p,props[p]);
    //}
    f.setId(feature.getId());
    f['ol_uid'] = feature['ol_uid'];
		f['NATIVE_SOURCE'] = feature['NATIVE_SOURCE'];
    return f;
  }

  EncodedPolylineToFeature(encoded:string, options?:IFormatOptions) : Feature {
    let polyline = new Polyline().readGeometry(encoded,options);
    return new Feature({
      geometry: polyline
    });
  }

  GeoJSONToFeatures(geojson, options?:IFormatOptions) : Feature[] {
    // PostGIS ST_GeomFromGeoJSON() requires that the GeoJSON must have
    // "crs":{"type":"name","properties":{"name":"EPSG:4326"}}
    // Softoo REST API will add it, so ensure that the coordinates 
    // should be transfromed to EPSG:4326, i.e. specify options: {
    //   dataProjection:'EPSG:4326',
    //   featureProjection:'EPSG:3857'
    // }
    return (new GeoJSON(options)).readFeatures(geojson);
  }

  FeatureToGeoJSON(feature:Feature, options?:IFormatOptions) : IGeoJSON {
    /*if (multi) {
      let geom = feature.getGeometry();
      let coord = [geom.getCoordinates()];
      geom = new MultiPolygon([geom.getCoordinates()]);
      feature.setGeometry(geom);
    }*/
    let gj:IGeoJSON = (new GeoJSON(options)).writeFeatureObject(feature);
    gj.featureOnMap = feature;
    return gj;
  }
  
  FeatureToWKT(feature:Feature, options?:IFormatOptions) : string {
    return (new WKT())['writeFeatureText'](feature,options);
  }

  getVectorLayerById(id:string) {
    let layer;
    for (let i=0; i<this.map.VectorLayers.length; i++) {
      let stoSource = this.map.VectorLayers[i]['SOFTOO_SOURCE'];
      if (stoSource && stoSource.id==id) {
        layer = this.map.VectorLayers[i];
        break;
      }
    }
    return layer;
  }

  addFeaturesToEditingLayers(features, options?:{[key:string] : any}) : Feature[] {
    let extent:Extent = [Number.MAX_VALUE,Number.MAX_VALUE,Number.MIN_VALUE,Number.MIN_VALUE];
    let points:Feature[] = [];
    let lines:Feature[] = [];
    let polygons:Feature[] = [];
    let unSupported:Feature[] = [];
    let undos:Feature[] = [];
    features.forEach((feature) => {
      let geom = feature.getGeometry();
      let ext = geom.getExtent();
      extent = [
        Math.min(extent[0],ext[0]),
        Math.min(extent[1],ext[1]),
        Math.max(extent[2],ext[2]),
        Math.max(extent[3],ext[3])
      ];
      if (this.isPointGeometry(geom)) {
        feature.set('STATE', 'Insert');
        undos.push(feature);
        points.push(feature);
      } else if (this.isLineGeometry(geom)) {
        feature.set('STATE', 'Insert');
        undos.push(feature);
        lines.push(feature);
      } else if (this.isPolygonGeometry(geom)) {
        feature.set('area_sqm',this.calcArea(geom));
        feature.set('STATE', 'Insert');
        undos.push(feature);
        polygons.push(feature);
      } else {
        unSupported.push(feature);
      }
    });
    let layer;
    if (points.length>0) {
      layer = options.pointLayer || this.getEditingLayers().Point[0];
      layer.getSource().addFeatures(points);
    }
    if (lines.length>0) {
      layer = options.lineLayer || this.getEditingLayers().Line[0];
      layer.getSource().addFeatures(lines);
    } 
    if (polygons.length>0) {
      layer = options.polygonLayer || this.getEditingLayers().Polygon[0];
      layer.getSource().addFeatures(polygons);
    }
    undos.forEach((feature,index) => {
      this.UndoRedo.addUndo(feature,'Insert',index>0);
    });

    //we have tuned the MAX_RESOLUTION_4_EDITING to allowing showing
    //editing features at small scall so we don't need to add them to
    //SelectedFeatureLayer
    /*let dex = extent[2] - extent[0];
    let dey = extent[3] - extent[1];
    if (dex>1500 || dey>1500) {
      // for large extent, add them to SelectedFeatureLayer in order to see them on map
      this.addFeaturesToLayer(points.concat(lines).concat(polygons), 'SelectedFeatureLayer', {
        resetMapExtent: false
      });
    }*/
    this.changeMapView('zoomToExtent',{extent:extent});
    return unSupported;
  }

  addFeaturesToLayer(features, 
    layerName:"DrawingLayer" | "SelectedFeatureLayer" | "AoiLayer" | "SearchFeatureLayer" | string, 
    options?:{[key:string] : string | boolean}) : Extent {
    if (!layerName) return;
    options = options || {};
    let layer:any;
    if (!!layerName.match(/DrawingLayer|SelectedFeatureLayer|AoiLayer|SearchFeatureLayer/)) {
      layer = this.map[layerName]
    } else {
      layer = this.getVectorLayerById(layerName);
    }
    let source = layer.getSource();
    if (options.clearSource!==false) {
      source.clear();
    }
    source.addFeatures(features);
    let extent = source.getExtent();
    let dx = extent[2] - extent[0];
    let dy = extent[3] - extent[1];
    if (dx>=2000 && dy>=2000) {
      dx = 0;
      dy = 0;
    } else if (dx<1000 || dy<1000) {
      // use dx and dy
    } else {
      dx /= 2;
      dy /= 2;
    }
    let extent2 = [
      extent[0] - dx,
      extent[1] - dy,
      extent[2] + dx,
      extent[3] + dy
    ];
    if (options.resetMapExtent || (options.resetMapExtent!==false && layerName=='AoiLayer')) {
      this.viewProps.extent = [extent[0], extent[1], extent[2], extent[3]];
    }
    if (options.zoomTo!==false) {
      dx = extent2[2] - extent2[0];
      dy = extent2[3] - extent2[1];
      if (dx<=0.1 && dy<=0.1) {
        this.changeMapView('setCenterAt',{
          center: [(extent2[2] + extent2[0])/2, (extent2[3] + extent2[1])/2],
          zoom: Math.max(12, this.getZoom())
        });
      } else {
        this.changeMapView('zoomToExtent',{extent:extent2});
      }
    }
    return [extent[0], extent[1], extent[2], extent[3]];
  }

  getFeatureCentroid(feature) : IMapCoordinate {
    let mc: Coordinate;
    let g = feature.getGeometry();
    if (g instanceof MultiPolygon) {
      g = g.getPolygon(0);
    }
    mc = Ring.centroid(g.getLinearRing(0).getCoordinates());
    let wgs84 = proj.toLonLat(mc);
    return  {
      map: mc,
      wgs84: [wgs84[0], wgs84[1]],
      utm: UTM.lonLatToUTM(wgs84[0],wgs84[1])
    };
  }

  getAoiCentroid() : IMapCoordinate {
    let c:IMapCoordinate = null;
    let features:Feature[] = this.map.AoiLayer.getSource().getFeatures();
    if (features && features.length>0) {
      return this.getFeatureCentroid(features[0]);
    }
    return c;
  }

  insideAoi(lonlat:Coordinate) : boolean {
    let features:Feature[] = this.map.AoiLayer.getSource().getFeatures();
    if (features && features.length>0) {
      let loc = proj.fromLonLat(lonlat);
      let g = features[0].getGeometry();
      if (g instanceof MultiPolygon) {
        g = g.getPolygon(0);
      }
      return Ring.inSide(
        [loc[0],loc[1]], (<Polygon>g).getLinearRing(0).getCoordinates()
      );
    }
    return true;
  }

  latLonToOpenLocationCode(latLon:Coordinate) : string {
    let OLC = new OpenLocationCode();
    return OLC.encode(latLon[0], latLon[1]);
  }

  mapToWGS84(coordinate:Coordinate) : Coordinate {
    let view = this.map.NativeMap.getView();
    let projection = view.getProjection();
    let wgs84 = proj.transform(coordinate, projection, 'EPSG:4326');
    return wgs84;
  }
  
  mapToLonLat(map:Coordinate) : Coordinate {
    let lonLat = proj.toLonLat(map);
    return [lonLat[0], lonLat[1]];
  }

  utmToLatLon(x:number,y:number,zone:number,ns:"N" | "S") : Coordinate {
    return UTM.utmToLonLat(x, y, zone, ns=='S');
  }

  lonLatToMap(lonlat:Coordinate) : Coordinate {
    let coord = proj.fromLonLat(lonlat);
    return [coord[0], coord[1]];
  }

  lonLatToUtm(lonLat:Coordinate) : IUTMCoordinate {
    return UTM.lonLatToUTM(lonLat[0], lonLat[1]);
  }

  hideFeature(feature:Feature) {
    feature.setStyle(STYLE.Hidden);
  }

  clearLayer(layerName:"SearchFeatureLayer" | "SelectedFeatureLayer" | "AoiLayer" | "DrawingLayer") {
    this.map[layerName].getSource().clear();
  }

  clearHighlightFeature() {
    if (this.map.HighlightFeatureTool && this.map.HighlightedFeature) {
      if (!!this.map.HighlightFeatureTool.getOverlay) {
        //ol v5
        this.map.HighlightFeatureTool.getOverlay().getSource().clear();
      } else {
        //ol v6
        let layer = this.map.HighlightFeatureTool.getLayer(this.map.HighlightedFeature);
        if (layer) {
          layer.getSource().clear();
        }
      }
      this.map.HighlightedFeature = null;
    }
  }

  clearAoi() {
    this.map.AoiLayer.getSource().clear();
  }

  clearLocator(id?:string) {
    id = id || 'SOFTOO_LOCATOR';
    let source = this.map.DrawingLayer.getSource();
    let features = source.getFeatures();
    if (features && features.length>0) {
      features.forEach((f) => {
        if (f.getGeometry() instanceof Point && f.get('STO_FLASH_PIN')==id) {
          source.removeFeature(f);
          return;
        }
      }, this);
    }
    let flashPins: IFlashPin[] = [];
    this.map.FlashPins.forEach((flash) => {
      if (!(flash.id==id)) {
          flashPins.push(flash);
      }
    }, this);
    this.map.FlashPins = flashPins;
  }
  
  gotoLocation(lonlat:Coordinate, zoom?:number) {
    //IMPORTANT: do zoom before 'setCenterAt'
    let view = this.map.NativeMap.getView();
    zoom = (zoom || zoom==0) ? zoom : Math.max(16, view.getZoom());
    let mc = this.getMapCenterForCoordinateAt(zoom, null, lonlat);
    let self = this;
    view.animate({
      center: mc.map,
      zoom: zoom, 
      duration: MV_ANIMATION_DURATION
    },(state) => {
      self.onViewAnimationEnd(view, state);
    });
    let source = this.map.DrawingLayer.getSource();
    let features = source.getFeatures();
    let locator: Feature;
    if (features && features.length>0) {
      features.forEach((f) => {
        if (f.getGeometry() instanceof Point && f.get('STO_FLASH_PIN')=='SOFTOO_LOCATOR') {
          //source.removeFeature(f);
          locator = f;
          return;
        }
      }, this);
    }
    let location = proj.fromLonLat(lonlat);
    if (!locator) {
      locator = new Feature({
        geometry: new Point(location),
        STO_FLASH_PIN: 'SOFTOO_LOCATOR'
      });
      locator.setStyle(STYLE.FlagBlue);
      this.map.FlashPins.push({
        id: 'SOFTOO_LOCATOR',
        pin: locator,
        start: Date.now()
      });
      source.addFeature(locator);
    } else {
      locator.setGeometry(new Point(location));
    }
  }

  getFeatureHoverInfo(feature:Feature) : IHoverInfo {
    let nativeSource = feature['NATIVE_SOURCE'];
    let hoverInfo:IHoverInfo = null;
    if (!!nativeSource) {
      let nativeLayers = nativeSource['NATIVE_LAYERS'];
      for (let i=0; i<nativeLayers.length; i++) {
        if (nativeLayers[i].SOFTOO_SOURCE) {
          let smLayers:IMapLayer[] = nativeLayers[i].SOFTOO_SOURCE.mapLayers;
          for (let j=0; j<smLayers.length; j++) {
            if (!!smLayers[j].hoverInfo) {
              hoverInfo = smLayers[j].hoverInfo(feature);
              break;
            }
          }
          if (!!hoverInfo) {
            break;
          }
        }
      }
    }
    return hoverInfo;
  }

  showFeatureHoverInfo(hoverInfo:IHoverInfo, options?:IOverlay) {
    options = options || {};
    let mpos = options.position || this.MousePosition.map;
    //let center = this.getPaddingCenterCoordinate().map;
    let positioning = OverlayPositioning.BOTTOM_CENTER;
    let offset = [0, -40];
    /*if (mpos[0]>center[0] && mpos[1]>center[1]) {
      positioning = OverlayPositioning.TOP_RIGHT;
      offset = [-20, 10];
    } else if (mpos[0]>center[0] && mpos[1]==center[1]) {
      positioning = OverlayPositioning.CENTER_RIGHT;
      offset = [-20, 0];
    } else if (mpos[0]>center[0] && mpos[1]<center[1]) {
      positioning = OverlayPositioning.BOTTOM_RIGHT;
      offset = [-20, -40];
    } else if (mpos[0]==center[0] && mpos[1]<center[1]) {
      positioning = OverlayPositioning.BOTTOM_CENTER;
      offset = [0, -40];
    } else if (mpos[0]<center[0] && mpos[1]<center[1]) {
      positioning = OverlayPositioning.BOTTOM_LEFT;
      offset = [20, -40];
    } else if (mpos[0]<center[0] && mpos[1]==center[1]) {
      positioning = OverlayPositioning.CENTER_LEFT;
      offset = [20, 0];
    } else if (mpos[0]<center[0] && mpos[1]>center[1]) {
      positioning = OverlayPositioning.TOP_LEFT;
      offset = [20, 10];
    } else if (mpos[0]==center[0] && mpos[1]>center[1]) {
      positioning = OverlayPositioning.TOP_CENTER;
      offset = [0, 10];
    }*/
    if (!this.map.HoverInfo) {
      this.map.HoverInfo = new Overlay({
        id: 'hoverInfo',
        position: mpos,
        offset: offset,
        positioning: positioning,
        element: document.createElement('div'),
        autoPan: true
      });
    } else {
      this.map.HoverInfo.setPositioning(positioning);
      this.map.HoverInfo.setPosition(mpos);
    }

    let elem = this.map.HoverInfo.getElement();
    while (elem.hasChildNodes()) {
      elem.removeChild(elem.lastChild);
    }
    let mixin:any = {
      propsData: hoverInfo
    }
    // how to create an VueComponent in run-time
    let instance = new HoverInfo(mixin);
    if (!instance.$store && this.$store) {
      instance.$store = this.$store;
    }
    instance.$mount();
    elem.appendChild(instance.$el);
    let id = this.map.HoverInfo.getId();
    setTimeout(() => {
      if (!this.map.NativeMap.getOverlayById(id)) {
        this.map.NativeMap.addOverlay(this.map.HoverInfo);
      }
      this.map.HoverInfo.setPosition(mpos);
    }, 500);
  }

  onCloseInfoPopup: Function = null;
  showInfoPopup(title:string, htmlInfo:string | IPopUpInfo[], options?:IOverlay) {
    options = Object.assign({
      position: this.getPaddingCenterCoordinate().map
    }, options || {});
    if (options.onClose) {
      this.onCloseInfoPopup = options.onClose;
      delete options.onClose;
    }
    let view = this.map.NativeMap.getView();
    let zoom = Math.max(16, view.getZoom());
    let loc = options.position;
    if (options.bubbling) {
      let flasher:Feature;
      let source = this.map.DrawingLayer.getSource();
      let features = source.getFeatures();
      if (features && features.length>0) {
        features.forEach((f) => {
          if (f.getGeometry() instanceof Point && f.get('STO_FLASH_PIN')=='SOFTOO_INFO_FLASHER') {
            flasher = f;
            return;
          }
        }, this);
      }
      if (!flasher) {
        flasher = new Feature({
          geometry: new Point(loc),
          STO_FLASH_PIN: 'SOFTOO_INFO_FLASHER'
        });
        //flasher.setStyle(STYLE.PinRed); use default layer style
        this.map.FlashPins.push({
          id: 'SOFTOO_INFO_FLASHER',
          pin: flasher,
          fillColor: [255,215,0,0.3],
          strokeColor: [255,215,0,1],
          start: Date.now()
        });
        source.addFeature(flasher);
      } else {
        flasher.setGeometry(new Point(loc));
      }
    }
    loc = proj.toLonLat(loc);
    loc = this.getMapCenterForCoordinateAt(zoom,null,loc).map;
    if (options.zoomTo!==false) {
      let self = this;
      view.animate({
        center: loc,
        zoom: zoom, 
        duration: MV_ANIMATION_DURATION
      },(state) => {
        self.onViewAnimationEnd(view, state);
      });
    }
    // now add popup overlay
    if (!this.map.InfoPopup) {
      this.map.InfoPopup = new Overlay({
        id: 'infoPopup',
        positioning: options.positioning || OverlayPositioning.BOTTOM_CENTER,
        element: document.createElement('div'),
        autoPan: true,
        autoPanMargin: 0 //this.viewProps.padding[0] // + 32
      });
    } else if (!options.bubbling) {
      this.clearLocator('SOFTOO_INFO_FLASHER');
    }
    if (options.offset) {
      this.map.InfoPopup.setOffset(options.offset);
    }
    let elem = this.map.InfoPopup.getElement();
    while (elem.hasChildNodes()) {
      elem.removeChild(elem.lastChild);
    }
    let props:any = {
      title: title,
      info: htmlInfo,
      infoType: options.infoType,
      map: <Map> this,
      okText: options.okText,
      cancelText: options.cancelText,
      state: options.state || {}
    };
    if (options.style) {
      props.containerStyle = options.style;
    }
    let mixin:any = {
      propsData: props
    }
    if (options.methods) {
      mixin.methods = options.methods
    }
    
    // how to create an VueComponent in run-time
    let instance = new Popup(mixin);
    if (!instance.$store && this.$store) {
      instance.$store = this.$store;
    }
    instance.$mount();
    elem.appendChild(instance.$el);
    let id = this.map.InfoPopup.getId();
    setTimeout(() => {
      if (!this.map.NativeMap.getOverlayById(id)) {
        this.map.NativeMap.addOverlay(this.map.InfoPopup);
      }
      this.map.InfoPopup.setPosition(options.position);
    }, 400); // > MV_ANIMATION_DURATION
  }

  panInfoPopupIntoView() {
    if (this.map.InfoPopup) {
      this.map.InfoPopup['panIntoView']();
    }
  }

  hideInfoPopup(popup?:any) {
    if (this.map.InfoPopup) {
      let remove = this.onCloseInfoPopup ? this.onCloseInfoPopup() :
                   (popup && popup.onClose) ? popup.onClose() : true;
      if (remove!==false) {
        let id = this.map.InfoPopup.getId();
        if (this.map.NativeMap.getOverlayById(id)) {
          this.clearLocator('SOFTOO_INFO_FLASHER');
          this.map.NativeMap.removeOverlay(this.map.InfoPopup);
        }
      }
    }
  }

  onPostCompose(event) {
    let vectorContext = event.vectorContext;
    let frameState = event.frameState;
    this.map.FlashPins.forEach((flash:IFlashPin) => {
      let pinSource = flash.pin['NATIVE_SOURCE'];
      let pinLayer = pinSource['NATIVE_LAYERS'] ?
                     pinSource['NATIVE_LAYERS'][0] : null;
      let pinVisible = pinLayer ? pinLayer.getVisible() : true;
      if (pinVisible) {
        let flashPin = flash.pin.getGeometry().clone();
        let elapsed = frameState.time - flash.start;
        let elapsedRatio = elapsed / FLASH_DURATION;
        let radius = EASE_OUT(elapsedRatio) * (flash.radius || 35) + 5;
        let opacity = EASE_OUT(1 - elapsedRatio);
        let fillOpacity = EASE_OUT(0.5 - elapsedRatio);

        let fillColor = flash.fillColor || [119, 170, 203, 0.3];
        let strokeColor = flash.strokeColor || [119, 170, 203, 0.5];
        fillColor[3] = fillOpacity;
        strokeColor[3] = opacity;
        //fillColor.push(fillOpacity);
        //strokeColor.push(opacity);
        let bubble = STYLE.ImageCircle({
            radius: radius,
            fill: { color: fillColor },
            stroke: {
              color: strokeColor,
              width: 2 + opacity
            }
          });
        vectorContext.setStyle(bubble);
        vectorContext.drawGeometry(flashPin);
        if (elapsed > FLASH_DURATION) {
          flash.start = Date.now();
        }
      }
    }, this);
    // tell OpenLayers to continue postcompose animation
    this.map.NativeMap.render();
  };

  async getCapabilities(serviceType:"WMS" | "WMTS", url:string)  {
    try {
      let parser;
      url += '?service=' + serviceType + '&request=getCapabilities';
      if (serviceType=='WMS') {
        parser = new WMSCapabilities();
      } else {
        parser = new WMTSCapabilities();
      }
      let xml = await http.request(url);
      return parser.read(xml);
    } catch (error) {
      throw error;
    }
  }
}